/**
 * VEDANGJYOTISHAM V6.0 — AUTHORITATIVE PANCHANGA CALCULATION CORE
 * 
 * Single authoritative source of truth for all Panchanga calculations across
 * VedangJyotisham platform (Today's Panchang, Panchang Drawer, Muhurta Advisor,
 * Kundali Birth Panchang, Navatara, Gemstone Advisor).
 * 
 * Strict architectural rule:
 * Location + Date + Time + Timezone -> Astronomical Core -> Panchanga Core -> Consumers
 */

import * as _Astronomy from 'astronomy-engine';
const Astronomy: any = (_Astronomy as any).Observer ? _Astronomy : ((_Astronomy as any).default || _Astronomy);
import { PlanetName, ZodiacSign } from '../types/kundali';
import { formatTime } from './dateUtils';

export const CANONICAL_NAKSHATRAS: { name: string; ruler: PlanetName }[] = [
  { name: 'Ashwini', ruler: 'Ketu' },
  { name: 'Bharani', ruler: 'Venus' },
  { name: 'Krittika', ruler: 'Sun' },
  { name: 'Rohini', ruler: 'Moon' },
  { name: 'Mrigashira', ruler: 'Mars' },
  { name: 'Ardra', ruler: 'Rahu' },
  { name: 'Punarvasu', ruler: 'Jupiter' },
  { name: 'Pushya', ruler: 'Saturn' },
  { name: 'Ashlesha', ruler: 'Mercury' },
  { name: 'Magha', ruler: 'Ketu' },
  { name: 'Purva Phalguni', ruler: 'Venus' },
  { name: 'Uttara Phalguni', ruler: 'Sun' },
  { name: 'Hasta', ruler: 'Moon' },
  { name: 'Chitra', ruler: 'Mars' },
  { name: 'Swati', ruler: 'Rahu' },
  { name: 'Vishakha', ruler: 'Jupiter' },
  { name: 'Anuradha', ruler: 'Saturn' },
  { name: 'Jyeshtha', ruler: 'Mercury' },
  { name: 'Mula', ruler: 'Ketu' },
  { name: 'Purva Ashadha', ruler: 'Venus' },
  { name: 'Uttara Ashadha', ruler: 'Sun' },
  { name: 'Shravana', ruler: 'Moon' },
  { name: 'Dhanishta', ruler: 'Mars' },
  { name: 'Shatabhisha', ruler: 'Rahu' },
  { name: 'Purva Bhadrapada', ruler: 'Jupiter' },
  { name: 'Uttara Bhadrapada', ruler: 'Saturn' },
  { name: 'Revati', ruler: 'Mercury' }
];

export const CANONICAL_ZODIAC_SIGNS: ZodiacSign[] = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
];

/**
 * Calculates Ayanamsha for a Julian Date based on preference (Lahiri, KP, Raman)
 */
export function getCanonicalAyanamsha(time: _Astronomy.AstroTime, systemOverride?: string): number {
  let pref = systemOverride;
  if (!pref && typeof localStorage !== 'undefined') {
    try {
      pref = localStorage.getItem('astro_ayanamsha_pref') || 'lahiri';
    } catch {
      pref = 'lahiri';
    }
  }
  pref = pref || 'lahiri';

  // Astronomy Engine's AstroTime.ut is already fractional days since J2000.
  const j2000Days = time.ut;
  const yearsSince2000 = j2000Days / 365.2425;

  if (pref === 'kp') {
    return 23.956944 + yearsSince2000 * (50.2388 / 3600);
  }
  if (pref === 'raman') {
    return 22.404722 + yearsSince2000 * (50.290966 / 3600);
  }
  // Standard Chitrapaksha / N.C. Lahiri
  return 23.857092 + yearsSince2000 * (50.290966 / 3600);
}

// ============================================================================
// 1. CANONICAL CONSTANTS & NOMENCLATURE
// ============================================================================

export const CANONICAL_TITHI_NAMES = [
  'Pratipada', 'Dwitiya', 'Tritiya', 'Chaturthi', 'Panchami',
  'Shasthi', 'Saptami', 'Ashtami', 'Navami', 'Dashami',
  'Ekadashi', 'Dwadashi', 'Trayodashi', 'Chaturdashi', 'Purnima'
];

export const CANONICAL_YOGA_NAMES = [
  'Vishkambha', 'Priti', 'Ayushman', 'Saubhagya', 'Shobhana', 'Atiganda', 'Sukarma', 'Dhriti',
  'Shula', 'Ganda', 'Vriddhi', 'Dhruva', 'Vyaghata', 'Harshana', 'Vajra', 'Siddhi',
  'Vyatipata', 'Variyan', 'Parigha', 'Shiva', 'Siddha', 'Sadhya', 'Shubha', 'Shukla',
  'Brahma', 'Indra', 'Vaidhriti'
];

/**
 * 7 Movable (Chara) Karanas that repeat 8 times (56 half-tithis)
 */
export const CHARA_KARANAS = [
  'Bava', 'Balava', 'Kaulava', 'Taitila', 'Garaja', 'Vanija', 'Vishti (Bhadra)'
];

/**
 * 4 Fixed (Sthira) Karanas that occur once per lunar month
 */
export const STHIRA_KARANAS = {
  Kintughna: 'Kintughna',     // 1st half of Shukla Pratipada (k = 0)
  Shakuni: 'Shakuni',         // 2nd half of Krishna Chaturdashi (k = 57)
  Chatushpada: 'Chatushpada', // 1st half of Amavasya (k = 58)
  Naga: 'Naga'                // 2nd half of Amavasya (k = 59)
};

export const CANONICAL_VARA_NAMES = [
  { en: 'Sunday', sa: 'Ravivasara', planet: 'Sun' as PlanetName },
  { en: 'Monday', sa: 'Somavasara', planet: 'Moon' as PlanetName },
  { en: 'Tuesday', sa: 'Mangalavasara', planet: 'Mars' as PlanetName },
  { en: 'Wednesday', sa: 'Budhavasara', planet: 'Mercury' as PlanetName },
  { en: 'Thursday', sa: 'Guruvasara', planet: 'Jupiter' as PlanetName },
  { en: 'Friday', sa: 'Shukravasara', planet: 'Venus' as PlanetName },
  { en: 'Saturday', sa: 'Shanivasara', planet: 'Saturn' as PlanetName }
];

export const VEDIC_MASAS = [
  'Chaitra', 'Vaishakha', 'Jyeshtha', 'Ashadha',
  'Shravana', 'Bhadrapada', 'Ashvina', 'Kartika',
  'Margashirsha', 'Pausha', 'Magha', 'Phalguna'
];

export const RITUS = [
  { nameEn: 'Vasanta (Spring)', nameHi: 'वसन्त ऋतु', months: ['Chaitra', 'Vaishakha'] },
  { nameEn: 'Grishma (Summer)', nameHi: 'ग्रीष्म ऋतु', months: ['Jyeshtha', 'Ashadha'] },
  { nameEn: 'Varsha (Monsoon)', nameHi: 'वर्षा ऋतु', months: ['Shravana', 'Bhadrapada'] },
  { nameEn: 'Sharad (Autumn)', nameHi: 'शरद ऋतु', months: ['Ashvina', 'Kartika'] },
  { nameEn: 'Hemanta (Pre-Winter)', nameHi: 'हेमन्त ऋतु', months: ['Margashirsha', 'Pausha'] },
  { nameEn: 'Shishira (Winter)', nameHi: 'शिशिर ऋतु', months: ['Magha', 'Phalguna'] }
];

export const DISH_SHOOL_BY_DAY: Record<number, { en: string; hi: string }> = {
  0: { en: 'West (Paschim) - Avoid travel or carry ginger/ghee remedy', hi: 'पश्चिम दिशा - यात्रा वर्जित (अतः घी खाकर निकलें)' },
  1: { en: 'East (Purva) - Avoid travel or carry milk remedy', hi: 'पूर्व दिशा - यात्रा वर्जित (अतः दूध पीकर निकलें)' },
  2: { en: 'North (Uttar) - Avoid travel or carry honey remedy', hi: 'उत्तर दिशा - यात्रा वर्जित (अतः शहद खाकर निकलें)' },
  3: { en: 'North (Uttar) - Avoid travel or carry milk remedy', hi: 'उत्तर दिशा - यात्रा वर्जित (अतः दूध खाकर निकलें)' },
  4: { en: 'South (Dakshin) - Avoid travel or carry curd remedy', hi: 'दक्षिण दिशा - यात्रा वर्जित (अतः दही खाकर निकलें)' },
  5: { en: 'West (Paschim) - Avoid travel or carry barley remedy', hi: 'पश्चिम दिशा - यात्रा वर्जित (अतः जौ खाकर निकलें)' },
  6: { en: 'East (Purva) - Avoid travel or carry sesame remedy', hi: 'पूर्व दिशा - यात्रा वर्जित (अतः तिल खाकर निकलें)' }
};

// Canonical 8 daytime segment order (0-indexed, where 0 is 1st part after sunrise)
// Sun=0, Mon=1, Tue=2, Wed=3, Thu=4, Fri=5, Sat=6
export const RAHU_KALAM_SLOTS = [7, 1, 6, 4, 5, 3, 2];
export const YAMAGANDA_SLOTS = [4, 3, 2, 1, 0, 6, 5];
export const GULIKA_SLOTS = [6, 5, 4, 3, 2, 1, 0];

// ============================================================================
// 2. DATA CONTRACTS & INTERFACES
// ============================================================================

export interface PanchangaCoordinates {
  latitude: number;
  longitude: number;
  elevationMeters?: number;
  timezoneOffsetMinutes?: number; // e.g. +330 for IST
  placeName?: string;
  profileStorageId?: string;
}

/** UTC interval corresponding to the civil date containing `date` at the supplied fixed offset. */
export function getCanonicalCivilDayUtcBounds(date: Date, timezoneOffsetMinutes = 330): { startUtc: Date; endUtc: Date } {
  const offsetMilliseconds = timezoneOffsetMinutes * 60_000;
  const localDate = new Date(date.getTime() + offsetMilliseconds);
  const civilDateUtc = Date.UTC(localDate.getUTCFullYear(), localDate.getUTCMonth(), localDate.getUTCDate());
  const startUtc = new Date(civilDateUtc - offsetMilliseconds);
  return { startUtc, endUtc: new Date(startUtc.getTime() + 24 * 60 * 60_000) };
}

export interface EphemerisSnapshot {
  sunLongitude: number;
  moonLongitude: number;
  sunDeclination: number;
  moonDeclination: number;
  ayanamsha: number;
  ayanamshaSystem: string;
}

export interface DayWindowInfo {
  start: string;
  end: string;
  rawStart: Date;
  rawEnd: Date;
  durationMinutes: number;
}

export interface TithiFactor {
  number: number; // 1 to 30
  name: string; // e.g. "Shukla Dashami" or "Krishna Pratipada"
  rawName: string; // e.g. "Dashami"
  paksha: 'Shukla Paksha' | 'Krishna Paksha';
  degreesRemaining: number;
  startDateTime?: Date;
  endDateTime?: Date;
  formattedEndTime?: string;
}

export interface TithiTransition {
  number: number;
  name: string;
  rawName: string;
  paksha: 'Shukla Paksha' | 'Krishna Paksha';
  startDateTime: Date;
  endDateTime: Date;
  formattedStartTime: string;
  formattedEndTime: string;
}

export interface NakshatraTransition {
  index: number;
  name: string;
  pada: number;
  rulingPlanet: PlanetName;
  startDateTime: Date;
  endDateTime: Date;
  formattedStartTime: string;
  formattedEndTime: string;
}

export interface YogaTransition {
  index: number;
  name: string;
  startDateTime: Date;
  endDateTime: Date;
  formattedStartTime: string;
  formattedEndTime: string;
}

export interface KaranaTransition {
  index60: number;
  name: string;
  type: 'Fixed' | 'Movable';
  isVishtiBhadra: boolean;
  startDateTime: Date;
  endDateTime: Date;
  formattedStartTime: string;
  formattedEndTime: string;
}

export interface NakshatraFactor {
  index: number; // 0 to 26
  name: string; // e.g. "Rohini"
  pada: number; // 1 to 4
  degreesInNakshatra: number;
  rulingPlanet: PlanetName;
  startDateTime?: Date;
  endDateTime?: Date;
  formattedEndTime?: string;
}

export interface YogaFactor {
  index: number; // 0 to 26
  name: string;
  degreesRemaining: number;
  startDateTime?: Date;
  endDateTime?: Date;
  formattedEndTime?: string;
}

export interface KaranaFactor {
  index60: number; // 0 to 59 in lunar month
  name: string; // e.g. "Bava", "Vishti (Bhadra)", "Kintughna"
  type: 'Fixed' | 'Movable';
  isVishtiBhadra: boolean;
  startDateTime?: Date;
  endDateTime?: Date;
  formattedEndTime?: string;
}

export interface VaraFactor {
  dayOfWeek: number; // 0 to 6
  nameEn: string;
  nameSanskrit: string;
  rulingPlanet: PlanetName;
  isAdjustedForSunrise: boolean; // true if time is before sunrise and thus belongs to prior day
}

export interface ChoghadiyaFactor {
  name: 'Amrit' | 'Shubh' | 'Labh' | 'Char' | 'Rog' | 'Kaal' | 'Udveg';
  hiName: string;
  quality: 'Auspicious' | 'Favorable' | 'Inauspicious';
  ruler: PlanetName;
  isAuspicious: boolean;
  slotIndex: number;
  start: Date;
  end: Date;
}

export interface HoraFactor {
  ruler: PlanetName;
  slotIndex: number;
  start: Date;
  end: Date;
}

export interface PanchangaEvidence {
  astronomicalEngine: 'Astronomy-Engine VSOP87/ELP2000-82b';
  ayanamshaSystem: string;
  ayanamshaDegrees: number;
  observerLatitude: number;
  observerLongitude: number;
  observerElevationM: number;
  sunriseUtc: string;
  sunsetUtc: string;
  dayDurationHours: number;
  calculationTimestamp: string;
  profileStorageId?: string;
}

export interface AuthoritativePanchanga {
  targetDateTime: Date;
  coordinates: PanchangaCoordinates;
  ephemeris: EphemerisSnapshot;
  sunrise: Date;
  sunset: Date;
  solarNoon: Date;
  
  // The Five Angas
  tithi: TithiFactor;
  tithiTransitions: TithiTransition[];
  nakshatra: NakshatraFactor;
  nakshatraTransitions: NakshatraTransition[];
  yoga: YogaFactor;
  yogaTransitions: YogaTransition[];
  karana: KaranaFactor;
  karanaTransitions: KaranaTransition[];
  vara: VaraFactor;

  // Time-based Inauspicious & Auspicious Muhurtas
  rahuKalam: DayWindowInfo;
  yamaganda: DayWindowInfo;
  gulikaKalam: DayWindowInfo;
  abhijit: DayWindowInfo;

  // Additional Classical Systems
  choghadiya: ChoghadiyaFactor;
  hora: HoraFactor;

  // Calendar Framework
  masa: string;
  paksha: 'Shukla Paksha' | 'Krishna Paksha';
  vikramSamvat: number;
  shakaSamvat: number;
  samvatString: string;
  ritu: { nameEn: string; nameHi: string };
  dishShool: { en: string; hi: string };
  ayana: 'UTTARAYANA' | 'DAKSHINAYANA';
  ayanaNameEn: string;
  ayanaNameHi: string;

  // Epistemic Provenance
  evidence: PanchangaEvidence;
  pitruPaksha: PitruPakshaContext;
  vratObservance: VratObservanceInfo;
  sankranti: SankrantiInfo;
}

export interface SankrantiInfo {
  isSankrantiDay: boolean;
  sankrantiNameEn: string;
  sankrantiNameHi: string;
  sankrantiNameSa: string;
  fromSignEn: string;
  fromSignHi: string;
  toSignEn: string;
  toSignHi: string;
  ingressTimestamp?: Date;
  formattedIngressTime?: string;
  punyaKaalStart?: Date;
  punyaKaalEnd?: Date;
  formattedPunyaKaal?: string;
  mahaPunyaKaalStart?: Date;
  mahaPunyaKaalEnd?: Date;
  formattedMahaPunyaKaal?: string;
  isPunyaKaalActive: boolean;
  classification: 'UTTARAYANA_START' | 'DAKSHINAYANA_START' | 'VISHUVA' | 'STANDARD';
  significanceEn: string;
  significanceHi: string;
  recommendedRitesEn: string[];
  recommendedRitesHi: string[];
}

export interface PitruPakshaMuhurtaWindow {
  start: string;
  end: string;
  durationMinutes: number;
}

export interface PitruPakshaContext {
  isActive: boolean;
  observanceName: string;
  observanceNameHi: string;
  regionalName: string;
  regionalNameHi: string;
  lunarMonth: string;
  paksha: string;
  tithiNumber: number;
  shraddhaTithiNameEn: string;
  shraddhaTithiNameHi: string;
  shraddhaSpecialDesignationEn?: string;
  shraddhaSpecialDesignationHi?: string;
  kutupaMuhurta?: PitruPakshaMuhurtaWindow;
  rohinaMuhurta?: PitruPakshaMuhurtaWindow;
  aparahnaKala?: PitruPakshaMuhurtaWindow;
  description: string;
  descriptionHi: string;
  tarpanGuidanceEn: string;
  tarpanGuidanceHi: string;
  applicabilityNote: string;
  applicabilityNoteHi: string;
  classicalCitations: string[];
}

export interface VratObservanceInfo {
  hasVrat: boolean;
  vratName: string;
  vratNameHi: string;
  vratCategory: 'EKADASHI' | 'PRADOSHA' | 'PURNIMA' | 'AMAVASYA' | 'SANKASHTI' | 'SHIVARATRI' | 'NAVARATRI' | 'NITYA_ARCHANA';
  fastingStart: string;
  fastingStartHi: string;
  paranaWindow: string;
  paranaWindowHi: string;
  paranaNotes: string;
  paranaNotesHi: string;
  dailyObservance: string;
  dailyObservanceHi: string;
  upcomingFast: string;
  upcomingFastHi: string;
  upcomingFastDate: string;
  panchangNoteEn: string;
  panchangNoteHi: string;
}

// ============================================================================
// 3. ASTRONOMICAL CORE PRIMITIVES
// ============================================================================

/**
 * Get astronomical observer with precise elevation
 */
export function getAstronomyObserver(lat: number, lon: number, elevationMeters: number = 0): _Astronomy.Observer {
  return new Astronomy.Observer(lat, lon, Math.max(0, elevationMeters));
}

/**
 * Computes exact sunrise, sunset, and solar noon for given date and coordinates.
 */
export function calculateAuthoritativeSunTimes(
  date: Date,
  lat: number,
  lon: number,
  elevationMeters: number = 0,
  timezoneOffsetMinutes = 330
): { sunrise: Date; sunset: Date; solarNoon: Date } {
  const observer = getAstronomyObserver(lat, lon, elevationMeters);
  
  // Reference search from local midnight of the target date
  const { startUtc: dayStart } = getCanonicalCivilDayUtcBounds(date, timezoneOffsetMinutes);
  const dayStartTime = Astronomy.MakeTime(dayStart);

  const actualSunrise = Astronomy.SearchRiseSet(Astronomy.Body.Sun, observer, +1, dayStartTime, 1.2);
  const sunrise = actualSunrise ? actualSunrise.date : new Date(dayStart.getTime() + 6 * 3600 * 1000);
  const riseTime = Astronomy.MakeTime(sunrise);
  const actualSunset = Astronomy.SearchRiseSet(Astronomy.Body.Sun, observer, -1, riseTime, 1.2);
  const sunset = actualSunset ? actualSunset.date : new Date(sunrise.getTime() + 12 * 3600 * 1000);
  const solarNoon = new Date(sunrise.getTime() + (sunset.getTime() - sunrise.getTime()) / 2);

  return { sunrise, sunset, solarNoon };
}

/**
 * Computes sidereal longitudes for Sun and Moon using canonical ayanamsha policy
 */
export function calculateAuthoritativeSunMoon(
  date: Date,
  ayanamshaSystemOverride?: string
): EphemerisSnapshot {
  const astroTime = Astronomy.MakeTime(date);
  const ayanamsha = getCanonicalAyanamsha(astroTime, ayanamshaSystemOverride);

  const sunEcl = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Sun, astroTime, true));
  const moonEcl = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Moon, astroTime, true));

  const sunLongitude = (sunEcl.elon - ayanamsha + 360) % 360;
  const moonLongitude = (moonEcl.elon - ayanamsha + 360) % 360;

  return {
    sunLongitude,
    moonLongitude,
    sunDeclination: sunEcl.elat,
    moonDeclination: moonEcl.elat,
    ayanamsha,
    ayanamshaSystem: ayanamshaSystemOverride || 'lahiri'
  };
}

// ============================================================================
// 4. CLASSICAL FIVE ANGAS CALCULATIONS
// ============================================================================

/**
 * Computes Tithi from Sun and Moon sidereal longitudes
 */
export function calculateCanonicalTithi(
  sunLon: number, 
  moonLon: number,
  exactDate?: Date,
  ayanamshaSystem?: string
): TithiFactor {
  const diff = (moonLon - sunLon + 360) % 360;
  const tNum = Math.floor(diff / 12) + 1; // 1 to 30
  const isShukla = tNum <= 15;
  const paksha: 'Shukla Paksha' | 'Krishna Paksha' = isShukla ? 'Shukla Paksha' : 'Krishna Paksha';
  const idx = (tNum - 1) % 15;
  
  let rawName = CANONICAL_TITHI_NAMES[idx];
  if (!isShukla && idx === 14) rawName = 'Amavasya';
  if (isShukla && idx === 14) rawName = 'Purnima';
  
  const name = `${paksha} ${rawName}`;
  const degreesRemaining = 12 - (diff % 12);

  let endDateTime: Date | undefined;
  if (exactDate) {
    endDateTime = findTithiEndBoundary(exactDate, tNum, ayanamshaSystem);
  }

  return {
    number: tNum,
    name,
    rawName,
    paksha,
    degreesRemaining,
    endDateTime,
    formattedEndTime: endDateTime ? formatTime(endDateTime, false) : undefined
  };
}

/**
 * Computes Nakshatra from Moon sidereal longitude
 */
export function calculateCanonicalNakshatra(
  moonLon: number,
  exactDate?: Date,
  ayanamshaSystem?: string
): NakshatraFactor {
  const nakIdx = Math.floor(moonLon / (360 / 27));
  const nakObj = CANONICAL_NAKSHATRAS[nakIdx] || CANONICAL_NAKSHATRAS[0];
  const degInNak = moonLon % (360 / 27);
  const pada = Math.floor(degInNak / (360 / 108)) + 1;

  let endDateTime: Date | undefined;
  if (exactDate) {
    endDateTime = findNakshatraEndBoundary(exactDate, nakIdx, ayanamshaSystem);
  }

  return {
    index: nakIdx,
    name: nakObj.name,
    pada,
    degreesInNakshatra: degInNak,
    rulingPlanet: nakObj.ruler,
    endDateTime,
    formattedEndTime: endDateTime ? formatTime(endDateTime, false) : undefined
  };
}

/**
 * Computes Yoga from Sun + Moon sidereal longitudes
 */
export function calculateCanonicalYoga(
  sunLon: number, 
  moonLon: number,
  exactDate?: Date,
  ayanamshaSystem?: string
): YogaFactor {
  const sumLon = (sunLon + moonLon) % 360;
  const yogaIdx = Math.floor(sumLon / (360 / 27)) % 27;
  const name = CANONICAL_YOGA_NAMES[yogaIdx];
  const degreesRemaining = (360 / 27) - (sumLon % (360 / 27));

  let endDateTime: Date | undefined;
  if (exactDate) {
    endDateTime = findYogaEndBoundary(exactDate, yogaIdx, ayanamshaSystem);
  }

  return {
    index: yogaIdx,
    name,
    degreesRemaining,
    endDateTime,
    formattedEndTime: endDateTime ? formatTime(endDateTime, false) : undefined
  };
}

/**
 * Computes Karana using classical 60-half-tithi sequence
 * 
 * Rules:
 * - k = 0: Kintughna (Fixed)
 * - k in 1..56: 8 cycles of the 7 Movable Karanas:
 *     (k-1)%7: 0=Bava, 1=Balava, 2=Kaulava, 3=Taitila, 4=Garaja, 5=Vanija, 6=Vishti
 * - k = 57: Shakuni (Fixed)
 * - k = 58: Chatushpada (Fixed)
 * - k = 59: Naga (Fixed)
 */
export function calculateCanonicalKarana(
  sunLon: number, 
  moonLon: number,
  exactDate?: Date,
  ayanamshaSystem?: string
): KaranaFactor {
  const diff = (moonLon - sunLon + 360) % 360;
  const k = Math.floor(diff / 6); // 0 to 59

  let name: string;
  let type: 'Fixed' | 'Movable';

  if (k === 0) {
    name = STHIRA_KARANAS.Kintughna;
    type = 'Fixed';
  } else if (k >= 1 && k <= 56) {
    const movableIdx = (k - 1) % 7;
    name = CHARA_KARANAS[movableIdx];
    type = 'Movable';
  } else if (k === 57) {
    name = STHIRA_KARANAS.Shakuni;
    type = 'Fixed';
  } else if (k === 58) {
    name = STHIRA_KARANAS.Chatushpada;
    type = 'Fixed';
  } else {
    name = STHIRA_KARANAS.Naga;
    type = 'Fixed';
  }

  let endDateTime: Date | undefined;
  if (exactDate) {
    endDateTime = findKaranaEndBoundary(exactDate, k, ayanamshaSystem);
  }

  return {
    index60: k,
    name,
    type,
    isVishtiBhadra: name.includes('Vishti') || name.includes('Bhadra'),
    endDateTime,
    formattedEndTime: endDateTime ? formatTime(endDateTime, false) : undefined
  };
}

/**
 * Computes Vedic Vara (Weekday) adjusted for local sunrise.
 * Pure and deterministic: isolates weekday from host environment's process.env.TZ
 * by computing the civil date using UTC timestamp plus timezoneOffsetMinutes.
 */
export function calculateCanonicalVara(
  targetDate: Date,
  sunrise: Date,
  timezoneOffsetMinutes: number = 330
): VaraFactor {
  const offsetMilliseconds = timezoneOffsetMinutes * 60_000;
  const localDate = new Date(targetDate.getTime() + offsetMilliseconds);
  let dayOfWeek = localDate.getUTCDay();
  let isAdjusted = false;

  // Classically, the Vedic solar day transitions at sunrise, not midnight.
  // For civil date midnight queries (00:00:00 local time), we preserve the calendar civil weekday.
  const isCivilMidnightQuery =
    localDate.getUTCHours() === 0 &&
    localDate.getUTCMinutes() === 0 &&
    localDate.getUTCSeconds() === 0;

  if (!isCivilMidnightQuery && targetDate.getTime() < sunrise.getTime()) {
    dayOfWeek = (dayOfWeek + 6) % 7;
    isAdjusted = true;
  }

  const meta = CANONICAL_VARA_NAMES[dayOfWeek];
  return {
    dayOfWeek,
    nameEn: meta.en,
    nameSanskrit: meta.sa,
    rulingPlanet: meta.planet,
    isAdjustedForSunrise: isAdjusted
  };
}

/**
 * Computes astronomical Ayana (solar motion course) from sidereal Sun longitude.
 * Nirayana Uttarayana begins when the Sun enters Makara (270°) and spans Makara through Mithuna (< 90°).
 * Nirayana Dakshinayana begins when the Sun enters Karka (90°) and spans Karka through Dhanu (< 270°).
 */
export function calculateAuthoritativeAyana(sunLongitude: number): {
  ayana: 'UTTARAYANA' | 'DAKSHINAYANA';
  ayanaNameEn: string;
  ayanaNameHi: string;
} {
  const normalizedSun = ((sunLongitude % 360) + 360) % 360;
  const isUttarayan = normalizedSun >= 270 || normalizedSun < 90;

  return {
    ayana: isUttarayan ? 'UTTARAYANA' : 'DAKSHINAYANA',
    ayanaNameEn: isUttarayan ? 'Uttarayan (Northern Solstice)' : 'Dakshinayan (Southern Solstice)',
    ayanaNameHi: isUttarayan ? 'उत्तरायण' : 'दक्षिणायन'
  };
}

/**
 * Calculates dynamic Vikram and Shaka Samvat with Chaitra Shukla Pratipada transition detection.
 * In classical Hindu calendar systems, the new Samvat year begins on Chaitra Shukla Pratipada
 * (falling in late March or April). Prior to this date in the Gregorian calendar year,
 * Vikram Samvat is Gregorian Year + 56 and Shaka Samvat is Gregorian Year - 79.
 * Following this transition until December 31, Vikram Samvat is Gregorian Year + 57
 * and Shaka Samvat is Gregorian Year - 78.
 */
export function calculateAuthoritativeSamvat(
  targetDate: Date,
  masa: string,
  paksha: 'Shukla Paksha' | 'Krishna Paksha'
): { vikramSamvat: number; shakaSamvat: number } {
  const gYear = targetDate.getFullYear();
  const gMonth = targetDate.getMonth(); // 0-indexed: 0 = Jan, 11 = Dec

  let hasNewSamvatBegun = false;

  if (gMonth < 2) {
    // January & February: Prior to Chaitra Shukla Pratipada
    hasNewSamvatBegun = false;
  } else if (gMonth > 4) {
    // June through December: Always after Chaitra Shukla Pratipada
    hasNewSamvatBegun = true;
  } else {
    // March, April, May: Evaluate lunar month and paksha transition
    if (masa === 'Phalguna' || masa === 'Magha' || masa === 'Pausha') {
      hasNewSamvatBegun = false;
    } else if (masa === 'Chaitra') {
      // In Chaitra, Shukla Paksha Pratipada marks the start of the new Samvat year
      hasNewSamvatBegun = paksha === 'Shukla Paksha';
    } else {
      // Vaishakha and subsequent lunar months
      hasNewSamvatBegun = true;
    }
  }

  const vikramSamvat = hasNewSamvatBegun ? gYear + 57 : gYear + 56;
  const shakaSamvat = hasNewSamvatBegun ? gYear - 78 : gYear - 79;

  return { vikramSamvat, shakaSamvat };
}

// ============================================================================
// 5. TIME-BASED MUHURTAS & SUB-DIVISIONS
// ============================================================================

/**
 * Calculates Rahu Kalam, Yamaganda, Gulika Kalam, and Abhijit Muhurta
 */
export function calculateCanonicalTimeWindows(
  sunrise: Date,
  sunset: Date,
  dayOfWeek: number
): {
  rahuKalam: DayWindowInfo;
  yamaganda: DayWindowInfo;
  gulikaKalam: DayWindowInfo;
  abhijit: DayWindowInfo;
} {
  const srMs = sunrise.getTime();
  const ssMs = sunset.getTime();
  const dayDurationMs = Math.max(1000, ssMs - srMs);
  const part8Ms = dayDurationMs / 8;
  const part15Ms = dayDurationMs / 15;

  const makeWindow8 = (slotIdx: number): DayWindowInfo => {
    const rawStart = new Date(srMs + slotIdx * part8Ms);
    const rawEnd = new Date(rawStart.getTime() + part8Ms);
    return {
      start: formatTime(rawStart, false),
      end: formatTime(rawEnd, false),
      rawStart,
      rawEnd,
      durationMinutes: Math.round(part8Ms / 60000)
    };
  };

  const rahuSlot = RAHU_KALAM_SLOTS[dayOfWeek];
  const yamaSlot = YAMAGANDA_SLOTS[dayOfWeek];
  const guliSlot = GULIKA_SLOTS[dayOfWeek];

  const rahuKalam = makeWindow8(rahuSlot);
  const yamaganda = makeWindow8(yamaSlot);
  const gulikaKalam = makeWindow8(guliSlot);

  // Abhijit Muhurta is the 8th Muhurta of 15 daylight parts (from 7/15 to 8/15)
  const abhijitStart = new Date(srMs + 7 * part15Ms);
  const abhijitEnd = new Date(srMs + 8 * part15Ms);
  const abhijit: DayWindowInfo = {
    start: formatTime(abhijitStart, false),
    end: formatTime(abhijitEnd, false),
    rawStart: abhijitStart,
    rawEnd: abhijitEnd,
    durationMinutes: Math.round(part15Ms / 60000)
  };

  return { rahuKalam, yamaganda, gulikaKalam, abhijit };
}

/**
 * Calculates Choghadiya for target time
 */
export function calculateCanonicalChoghadiya(
  targetDate: Date,
  sunrise: Date,
  sunset: Date,
  dayOfWeek: number
): ChoghadiyaFactor {
  const DAY_CHOGHADIYA_CYCLES: ('Amrit' | 'Shubh' | 'Labh' | 'Char' | 'Rog' | 'Kaal' | 'Udveg')[][] = [
    ['Udveg', 'Char', 'Labh', 'Amrit', 'Kaal', 'Shubh', 'Rog', 'Udveg'],       // Sunday
    ['Amrit', 'Kaal', 'Shubh', 'Rog', 'Udveg', 'Char', 'Labh', 'Amrit'],       // Monday
    ['Rog', 'Udveg', 'Char', 'Labh', 'Amrit', 'Kaal', 'Shubh', 'Rog'],         // Tuesday
    ['Labh', 'Amrit', 'Kaal', 'Shubh', 'Rog', 'Udveg', 'Char', 'Labh'],         // Wednesday
    ['Shubh', 'Rog', 'Udveg', 'Char', 'Labh', 'Amrit', 'Kaal', 'Shubh'],       // Thursday
    ['Char', 'Labh', 'Amrit', 'Kaal', 'Shubh', 'Rog', 'Udveg', 'Char'],         // Friday
    ['Kaal', 'Shubh', 'Rog', 'Udveg', 'Char', 'Labh', 'Amrit', 'Kaal']          // Saturday
  ];

  const NIGHT_CHOGHADIYA_CYCLES: ('Amrit' | 'Shubh' | 'Labh' | 'Char' | 'Rog' | 'Kaal' | 'Udveg')[][] = [
    ['Shubh', 'Amrit', 'Char', 'Rog', 'Kaal', 'Labh', 'Udveg', 'Shubh'],       // Sunday night
    ['Char', 'Rog', 'Kaal', 'Labh', 'Udveg', 'Shubh', 'Amrit', 'Char'],       // Monday night
    ['Kaal', 'Labh', 'Udveg', 'Shubh', 'Amrit', 'Char', 'Rog', 'Kaal'],       // Tuesday night
    ['Udveg', 'Shubh', 'Amrit', 'Char', 'Rog', 'Kaal', 'Labh', 'Udveg'],       // Wednesday night
    ['Amrit', 'Char', 'Rog', 'Kaal', 'Labh', 'Udveg', 'Shubh', 'Amrit'],       // Thursday night
    ['Rog', 'Kaal', 'Labh', 'Udveg', 'Shubh', 'Amrit', 'Char', 'Rog'],         // Friday night
    ['Labh', 'Udveg', 'Shubh', 'Amrit', 'Char', 'Rog', 'Kaal', 'Labh']          // Saturday night
  ];

  const CHOGHADIYA_META: Record<string, { hiName: string; quality: 'Auspicious' | 'Favorable' | 'Inauspicious'; ruler: PlanetName; isAuspicious: boolean }> = {
    Amrit: { hiName: 'अमृत', quality: 'Auspicious', ruler: 'Moon', isAuspicious: true },
    Shubh: { hiName: 'शुभ', quality: 'Auspicious', ruler: 'Jupiter', isAuspicious: true },
    Labh: { hiName: 'लाभ', quality: 'Auspicious', ruler: 'Mercury', isAuspicious: true },
    Char: { hiName: 'चर', quality: 'Favorable', ruler: 'Venus', isAuspicious: true },
    Udveg: { hiName: 'उद्वेग', quality: 'Inauspicious', ruler: 'Sun', isAuspicious: false },
    Rog: { hiName: 'रोग', quality: 'Inauspicious', ruler: 'Mars', isAuspicious: false },
    Kaal: { hiName: 'काल', quality: 'Inauspicious', ruler: 'Saturn', isAuspicious: false }
  };

  const t = targetDate.getTime();
  const sr = sunrise.getTime();
  const ss = sunset.getTime();

  let name: 'Amrit' | 'Shubh' | 'Labh' | 'Char' | 'Rog' | 'Kaal' | 'Udveg' = 'Shubh';
  let slotIdx = 0;
  let start = sunrise;
  let end = sunset;

  if (t >= sr && t < ss) {
    const partMs = (ss - sr) / 8;
    slotIdx = Math.min(7, Math.max(0, Math.floor((t - sr) / partMs)));
    name = DAY_CHOGHADIYA_CYCLES[dayOfWeek][slotIdx];
    start = new Date(sr + slotIdx * partMs);
    end = new Date(start.getTime() + partMs);
  } else {
    const nextSr = sr + 24 * 3600 * 1000;
    const partMs = (nextSr - ss) / 8;
    const diff = t >= ss ? t - ss : (t + 24 * 3600 * 1000) - ss;
    slotIdx = Math.min(7, Math.max(0, Math.floor(diff / partMs)));
    name = NIGHT_CHOGHADIYA_CYCLES[dayOfWeek][slotIdx];
    start = new Date(ss + slotIdx * partMs);
    end = new Date(start.getTime() + partMs);
  }

  const meta = CHOGHADIYA_META[name] || { hiName: name, quality: 'Auspicious', ruler: 'Jupiter', isAuspicious: true };
  return {
    name,
    hiName: meta.hiName,
    quality: meta.quality,
    ruler: meta.ruler,
    isAuspicious: meta.isAuspicious,
    slotIndex: slotIdx,
    start,
    end
  };
}

/**
 * Calculates Hora for target time
 */
export function calculateCanonicalHora(
  targetDate: Date,
  sunrise: Date,
  dayOfWeek: number
): HoraFactor {
  const HORA_ORDER: PlanetName[] = ['Sun', 'Venus', 'Mercury', 'Moon', 'Saturn', 'Jupiter', 'Mars'];
  const dayStartHora = [0, 3, 6, 2, 5, 1, 4][dayOfWeek]; // Ruler of 1st hora matches weekday ruler
  
  const elapsedMinutes = Math.max(0, (targetDate.getTime() - sunrise.getTime()) / 60000);
  const horaSlot = Math.floor((elapsedMinutes % (24 * 60)) / 60);
  const currentRulerIdx = (dayStartHora + horaSlot) % 7;
  const ruler = HORA_ORDER[currentRulerIdx];

  const start = new Date(sunrise.getTime() + horaSlot * 3600000);
  const end = new Date(start.getTime() + 3600000);

  return {
    ruler,
    slotIndex: horaSlot,
    start,
    end
  };
}

// ============================================================================
// 6. NUMERICAL BOUNDARY SEARCH ALGORITHMS
// ============================================================================

function findTithiEndBoundary(date: Date, tNum: number, ayanamshaSystem?: string): Date {
  let low = date.getTime();
  let high = low + 28 * 3600000; // max ~28 hours forward
  for (let i = 0; i < 15; i++) {
    const mid = Math.floor((low + high) / 2);
    const snap = calculateAuthoritativeSunMoon(new Date(mid), ayanamshaSystem);
    const diff = (snap.moonLongitude - snap.sunLongitude + 360) % 360;
    const curNum = Math.floor(diff / 12) + 1;
    if (curNum === tNum) {
      low = mid;
    } else {
      high = mid;
    }
  }
  return new Date(Math.floor((low + high) / 2));
}

function findNakshatraEndBoundary(date: Date, nakIdx: number, ayanamshaSystem?: string): Date {
  let low = date.getTime();
  let high = low + 28 * 3600000;
  for (let i = 0; i < 15; i++) {
    const mid = Math.floor((low + high) / 2);
    const snap = calculateAuthoritativeSunMoon(new Date(mid), ayanamshaSystem);
    const curNak = Math.floor(snap.moonLongitude / (360 / 27));
    if (curNak === nakIdx) {
      low = mid;
    } else {
      high = mid;
    }
  }
  return new Date(Math.floor((low + high) / 2));
}

function findYogaEndBoundary(date: Date, yogaIdx: number, ayanamshaSystem?: string): Date {
  let low = date.getTime();
  let high = low + 28 * 3600000;
  for (let i = 0; i < 15; i++) {
    const mid = Math.floor((low + high) / 2);
    const snap = calculateAuthoritativeSunMoon(new Date(mid), ayanamshaSystem);
    const sum = (snap.sunLongitude + snap.moonLongitude) % 360;
    const curYoga = Math.floor(sum / (360 / 27)) % 27;
    if (curYoga === yogaIdx) {
      low = mid;
    } else {
      high = mid;
    }
  }
  return new Date(Math.floor((low + high) / 2));
}

function findKaranaEndBoundary(date: Date, kIdx: number, ayanamshaSystem?: string): Date {
  let low = date.getTime();
  let high = low + 15 * 3600000; // max ~15 hours forward
  for (let i = 0; i < 15; i++) {
    const mid = Math.floor((low + high) / 2);
    const snap = calculateAuthoritativeSunMoon(new Date(mid), ayanamshaSystem);
    const diff = (snap.moonLongitude - snap.sunLongitude + 360) % 360;
    const curK = Math.floor(diff / 6);
    if (curK === kIdx) {
      low = mid;
    } else {
      high = mid;
    }
  }
  return new Date(Math.floor((low + high) / 2));
}

// ============================================================================
// 7. CANONICAL IN-MEMORY CACHE
// ============================================================================

interface CacheEntry {
  key: string;
  data: AuthoritativePanchanga;
  expiresAt: number;
}

const PANCHANGA_CACHE = new Map<string, CacheEntry>();
const MAX_CACHE_SIZE = 250;

// ============================================================================
// VRAT & OBSERVANCE ENGINE
// ============================================================================

export const CLASSICAL_EKADASHIS: Record<string, { shuklaEn: string; shuklaHi: string; krishnaEn: string; krishnaHi: string }> = {
  Chaitra: { shuklaEn: 'Kamada Ekadashi', shuklaHi: 'कामदा एकादशी', krishnaEn: 'Papamochani Ekadashi', krishnaHi: 'पापमोचिनी एकादशी' },
  Vaishakha: { shuklaEn: 'Mohini Ekadashi', shuklaHi: 'मोहिनी एकादशी', krishnaEn: 'Varuthini Ekadashi', krishnaHi: 'वरूथिनी एकादशी' },
  Jyeshtha: { shuklaEn: 'Nirjala Ekadashi', shuklaHi: 'निर्जला एकादशी', krishnaEn: 'Apara Ekadashi', krishnaHi: 'अपरा एकादशी' },
  Ashadha: { shuklaEn: 'Devshayani Ekadashi (Harishayani)', shuklaHi: 'देवशयनी एकादशी (हरिशयनी)', krishnaEn: 'Yogini Ekadashi', krishnaHi: 'योगिनी एकादशी' },
  Shravana: { shuklaEn: 'Shravana Putrada Ekadashi (Pavitropana)', shuklaHi: 'श्रावण पुत्रदा एकादशी (पवित्रोपना)', krishnaEn: 'Kamika Ekadashi', krishnaHi: 'कामिका एकादशी' },
  Bhadrapada: { shuklaEn: 'Parivartini Ekadashi (Vamana / Parsva)', shuklaHi: 'परिवर्तिनी एकादशी (वामन / पार्श्व)', krishnaEn: 'Aja Ekadashi (Annada)', krishnaHi: 'अजा एकादशी (अन्नदा)' },
  Ashvina: { shuklaEn: 'Papankusha Ekadashi', shuklaHi: 'पापांकुशा एकादशी', krishnaEn: 'Indira Ekadashi', krishnaHi: 'इन्दिरा एकादशी' },
  Kartika: { shuklaEn: 'Devutthana Ekadashi (Prabodhini)', shuklaHi: 'देवउठनी एकादशी (प्रबोधिनी)', krishnaEn: 'Rama Ekadashi', krishnaHi: 'रमा एकादशी' },
  Margashirsha: { shuklaEn: 'Mokshada Ekadashi (Gita Jayanti)', shuklaHi: 'मोक्षदा एकादशी (गीता जयंती)', krishnaEn: 'Utpanna Ekadashi', krishnaHi: 'उत्पन्ना एकादशी' },
  Pausha: { shuklaEn: 'Pausha Putrada Ekadashi', shuklaHi: 'पौष पुत्रदा एकादशी', krishnaEn: 'Saphala Ekadashi', krishnaHi: 'सफला एकादशी' },
  Magha: { shuklaEn: 'Jaya Ekadashi (Bhaimi)', shuklaHi: 'जया एकादशी (भैमी)', krishnaEn: 'Shat-tila Ekadashi', krishnaHi: 'षट्-तिला एकादशी' },
  Phalguna: { shuklaEn: 'Amalaki Ekadashi', shuklaHi: 'आमलकी एकादशी', krishnaEn: 'Vijaya Ekadashi', krishnaHi: 'विजया एकादशी' }
};

export const DAY_OBSERVANCES_EN = [
  'Ravivasara (Surya Puja & Gayatri Japa)',
  'Somavasara (Lord Shiva Bilva Patra Archana)',
  'Mangalavasara (Hanuman Chalisa & Mangal Shanti)',
  'Budhavasara (Lord Ganesha Durva Archana)',
  'Guruvasara (Brihaspati Puja & Vishnu Sahasranama)',
  'Shukravasara (Maha Lakshmi Archana & Shree Sukta)',
  'Shanivasara (Lord Shani Taila Abhisheka & Hanuman Archana)'
];

export const DAY_OBSERVANCES_HI = [
  'रविवासर (सूर्य उपासना एवं गायत्री जप)',
  'सोमवासर (भगवान शिव बिल्वपत्र अर्चन)',
  'मंगलवासर (हनुमान चालीसा एवं मंगल शांति)',
  'बुधवासर (भगवान गणेश दूर्वा अर्चन)',
  'गुरुवासर (बृहस्पति पूजन एवं विष्णु सहस्रनाम)',
  'शुक्रवासर (महालक्ष्मी अर्चन एवं श्री सूक्त)',
  'शनिवासर (शनिदेव तैल अभिषेक एवं हनुमान अर्चना)'
];

export function calculateAuthoritativeVratObservance(
  targetDate: Date,
  tithiNumber: number,
  tithiName: string,
  paksha: 'Shukla Paksha' | 'Krishna Paksha',
  masa: string,
  varaDayOfWeek: number,
  varaNameEn: string,
  sunriseStr: string,
  ayanamshaOverride?: string
): VratObservanceInfo {
  const isEkadashi = tithiNumber === 11 || tithiNumber === 26;
  const isPradosha = tithiNumber === 13 || tithiNumber === 28;
  const isPurnima = tithiNumber === 15;
  const isAmavasya = tithiNumber === 30;
  const isSankashti = tithiNumber === 19;
  const isVinayaka = tithiNumber === 4;
  const isShivaratri = tithiNumber === 29;

  const ekadashiEntry = CLASSICAL_EKADASHIS[masa] || {
    shuklaEn: 'Ekadashi',
    shuklaHi: 'एकादशी',
    krishnaEn: 'Ekadashi',
    krishnaHi: 'एकादशी'
  };

  const dailyObservance = DAY_OBSERVANCES_EN[varaDayOfWeek] || DAY_OBSERVANCES_EN[0];
  const dailyObservanceHi = DAY_OBSERVANCES_HI[varaDayOfWeek] || DAY_OBSERVANCES_HI[0];

  // Dynamically calculate the NEXT upcoming Ekadashi by advancing forward day by day
  let upcomingFastDate = '';
  let upcomingFastEn = 'Ekadashi Vrat (Upcoming)';
  let upcomingFastHi = 'एकादशी व्रत (आगामी)';

  for (let offset = 1; offset <= 30; offset++) {
    const fDate = new Date(targetDate.getTime() + offset * 86400000);
    const fEphem = calculateAuthoritativeSunMoon(fDate, ayanamshaOverride);
    const diffDeg = (fEphem.moonLongitude - fEphem.sunLongitude + 360) % 360;
    const fTithiNum = Math.floor(diffDeg / 12) + 1;

    if (fTithiNum === 11 || fTithiNum === 26) {
      const fSunSignIdx = Math.floor(fEphem.sunLongitude / 30);
      const fMasaIdx = (fSunSignIdx + 1) % 12;
      const fMasa = VEDIC_MASAS[fMasaIdx];
      const fIsShukla = fTithiNum === 11;
      const fEkData = CLASSICAL_EKADASHIS[fMasa] || {
        shuklaEn: 'Ekadashi',
        shuklaHi: 'एकादशी',
        krishnaEn: 'Ekadashi',
        krishnaHi: 'एकादशी'
      };
      const fEkNameEn = fIsShukla ? fEkData.shuklaEn : fEkData.krishnaEn;
      const fEkNameHi = fIsShukla ? fEkData.shuklaHi : fEkData.krishnaHi;

      const dateStrEn = fDate.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
      const weekdayHi = ['रविवार', 'सोमवार', 'मंगलवार', 'बुधवार', 'गुरुवार', 'शुक्रवार', 'शनिवार'][fDate.getDay()];
      const monthHi = ['जनवरी', 'फरवरी', 'मार्च', 'अप्रैल', 'मई', 'जून', 'जुलाई', 'अगस्त', 'सितम्बर', 'अक्टूबर', 'नवम्बर', 'दिसम्बर'][fDate.getMonth()];
      const dateStrHi = `${weekdayHi}, ${fDate.getDate()} ${monthHi} ${fDate.getFullYear()}`;

      upcomingFastDate = fDate.toISOString();
      upcomingFastEn = `${fEkNameEn} on ${dateStrEn}`;
      upcomingFastHi = `${dateStrHi} (${fEkNameHi})`;
      break;
    }
  }

  let hasVrat = false;
  let vratName = 'No major fast today';
  let vratNameHi = 'आज कोई मुख्य व्रत नहीं';
  let vratCategory: VratObservanceInfo['vratCategory'] = 'NITYA_ARCHANA';
  let fastingStart = `Daily Sandhya / Regular Archana (${sunriseStr})`;
  let fastingStartHi = `नित्य सांध्य / नियमित पूजन (${sunriseStr})`;
  let paranaWindow = 'N/A';
  let paranaWindowHi = 'लागू नहीं';
  let paranaNotes = 'Standard daily archana. No mandatory fasting prescribed for today.';
  let paranaNotesHi = 'नित्य दैनिक पूजा। आज स्मार्त अथवा वैष्णव परम्परा में कोई अनिवार्य उपवास विहित नहीं है।';

  if (isEkadashi) {
    hasVrat = true;
    vratCategory = 'EKADASHI';
    const isShukla = tithiNumber === 11;
    vratName = isShukla ? ekadashiEntry.shuklaEn : ekadashiEntry.krishnaEn;
    vratNameHi = isShukla ? ekadashiEntry.shuklaHi : ekadashiEntry.krishnaHi;
    fastingStart = `Today at Sunrise (${sunriseStr})`;
    fastingStartHi = `आज सूर्योदय से (${sunriseStr})`;
    paranaWindow = 'Tomorrow morning during Dwadashi Parana window';
    paranaWindowHi = 'अगले दिन प्रातः द्वादशी पारण काल में';
    paranaNotes = 'Break fast with holy water and Tulsi leaves after morning Vishnu Archana.';
    paranaNotesHi = 'प्रातः श्रीविष्णु पूजन के पश्चात तुलसी पत्र एवं पवित्र जल से पारण करें।';
  } else if (isPradosha) {
    hasVrat = true;
    vratCategory = 'PRADOSHA';
    vratName = `${varaNameEn} Pradosha Vrat`;
    vratNameHi = `${['रवि', 'सोम', 'भौम', 'बुध', 'गुरु', 'शुक्र', 'शनि'][varaDayOfWeek]} प्रदोष व्रत`;
    fastingStart = 'Day-long fast concluding during Pradosha Sandhya';
    fastingStartHi = 'दिनभर उपवास, सायंकाल प्रदोष काल में समापन';
    paranaWindow = 'Evening after Pradosha Shiva Puja & Aarti';
    paranaWindowHi = 'सायंकाल प्रदोष शिव पूजा एवं आरती के उपरांत';
    paranaNotes = 'Perform Shiva Linga Abhisheka with Panchamrita during twilight Pradosha Sandhya.';
    paranaNotesHi = 'प्रदोष काल में पंचामृत से शिवलिंग का अभिषेक एवं पंचाक्षर स्तोत्र जप करें।';
  } else if (isPurnima) {
    hasVrat = true;
    vratCategory = 'PURNIMA';
    vratName = 'Satyanarayan Vrat / Purnima Fast';
    vratNameHi = 'श्री सत्यनारायण व्रत / पूर्णिमा उपवास';
    fastingStart = `Today at Sunrise (${sunriseStr})`;
    fastingStartHi = `आज सूर्योदय से (${sunriseStr})`;
    paranaWindow = 'Evening after Chandra Darshan (Moonrise)';
    paranaWindowHi = 'सायंकाल चंद्र दर्शन एवं अर्घ्य के उपरांत';
    paranaNotes = 'Break fast after evening Moon darshan, offering arghya, and listening to Satyanarayan Katha.';
    paranaNotesHi = 'सायंकाल चंद्रमा को अर्घ्य देकर एवं श्री सत्यनारायण कथा श्रवण के उपरांत पारण करें।';
  } else if (isAmavasya) {
    hasVrat = true;
    vratCategory = 'AMAVASYA';
    vratName = 'Amavasya Pitru Tarpan & Darsha Shraddha';
    vratNameHi = 'अमावस्या पितृ तर्पण एवं दर्श श्राद्ध';
    fastingStart = `Morning Sandhya (${sunriseStr})`;
    fastingStartHi = `प्रातः सांध्य काल (${sunriseStr})`;
    paranaWindow = 'Afternoon after Aparahna Tarpan & Shraddha';
    paranaWindowHi = 'अपराह्न काल में तर्पण एवं श्राद्ध के उपरांत';
    paranaNotes = 'Offer water/sesame tarpan to ancestors (Pitrus) during midday Aparahna kala.';
    paranaNotesHi = 'अपराह्न काल में पितरों के निमित्त तिल-जल तर्पण एवं ब्राह्मण/जरूरतमंद को भोजन अर्पित करें।';
  } else if (isSankashti) {
    hasVrat = true;
    vratCategory = 'SANKASHTI';
    vratName = 'Sankashti Ganesh Chaturthi Vrat';
    vratNameHi = 'संकष्टी गणेश चतुर्थी व्रत';
    fastingStart = `Today at Sunrise (${sunriseStr})`;
    fastingStartHi = `आज सूर्योदय से (${sunriseStr})`;
    paranaWindow = 'Night after Chandrodaya (Moonrise)';
    paranaWindowHi = 'रात्रि चंद्रोदय के पश्चात';
    paranaNotes = 'Break fast after sighting the Moon and offering prayers to Lord Ganesha (Sankatanashana Stotram).';
    paranaNotesHi = 'रात्रि में चंद्रोदय के दर्शन कर गणेश जी को मोदक/दूर्वा अर्पित करने के बाद पारण करें।';
  } else if (isVinayaka) {
    hasVrat = true;
    vratCategory = 'SANKASHTI';
    vratName = 'Vinayaka Chaturthi';
    vratNameHi = 'विनायक चतुर्थी';
    fastingStart = `Today at Sunrise (${sunriseStr})`;
    fastingStartHi = `आज सूर्योदय से (${sunriseStr})`;
    paranaWindow = 'Midday after Madhyahna Ganesha Puja';
    paranaWindowHi = 'मध्याह्न गणेश पूजन के उपरांत';
    paranaNotes = 'Perform Lord Ganesha Archana with Durva grass during midday Madhyahna window.';
    paranaNotesHi = 'मध्याह्न काल में भगवान श्रीगणेश को २१ दूर्वा एवं मोदक अर्पित कर पूजन करें।';
  } else if (isShivaratri) {
    hasVrat = true;
    vratCategory = 'SHIVARATRI';
    vratName = masa === 'Phalguna' || masa === 'Magha' ? 'Maha Shivaratri Vrat' : 'Masik Shivaratri';
    vratNameHi = masa === 'Phalguna' || masa === 'Magha' ? 'महाशिवरात्रि व्रत' : 'मासिक शिवरात्रि';
    fastingStart = `Today at Sunrise (${sunriseStr})`;
    fastingStartHi = `आज सूर्योदय से (${sunriseStr})`;
    paranaWindow = 'Next morning after Nishita Kala Shiva Puja';
    paranaWindowHi = 'अगले दिन प्रातः निशीथ काल पूजन के उपरांत';
    paranaNotes = 'Observe fasting and perform Bilva Patra archana during Nishita Kala (midnight).';
    paranaNotesHi = 'रात्रि निशीथ काल में भगवान शिव को बिल्वपत्र एवं जलाभिषेक अर्पित करें।';
  }

  const panchangNoteEn = hasVrat
    ? `Today is ${tithiName} (${vratName}). ${paranaNotes}`
    : `Today is ${tithiName}. No mandatory fasting is prescribed for today in Smriti texts. The next major fast is ${upcomingFastEn}.`;

  const panchangNoteHi = hasVrat
    ? `आज ${tithiName} (${vratNameHi}) है। ${paranaNotesHi}`
    : `आज ${tithiName} है। आज स्मार्त अथवा वैष्णव परम्परा में कोई अनिवार्य उपवास विहित नहीं है। आगामी मुख्य व्रत ${upcomingFastHi} को होगा।`;

  return {
    hasVrat,
    vratName,
    vratNameHi,
    vratCategory,
    fastingStart,
    fastingStartHi,
    paranaWindow,
    paranaWindowHi,
    paranaNotes,
    paranaNotesHi,
    dailyObservance,
    dailyObservanceHi,
    upcomingFast: upcomingFastEn,
    upcomingFastHi,
    upcomingFastDate,
    panchangNoteEn,
    panchangNoteHi
  };
}

// ============================================================================
// PITRU PAKSHA (SHRADDHA MAHATMYA) ENGINE
// ============================================================================

export function calculateAuthoritativePitruPakshaContext(
  targetDate: Date,
  tithiNumber: number,
  tithiRawName: string,
  paksha: 'Shukla Paksha' | 'Krishna Paksha',
  masa: string,
  nakshatraName: string,
  sunrise: Date,
  sunset: Date,
  timezoneOffsetMinutes: number = 330
): PitruPakshaContext {
  const isPitruPakshaActive = (masa === 'Bhadrapada' && tithiRawName === 'Purnima') || (masa === 'Ashvina' && paksha === 'Krishna Paksha');

  if (!isPitruPakshaActive) {
    return {
      isActive: false,
      observanceName: 'Pitru Paksha (Shraddha Paksha)',
      observanceNameHi: 'पितृ पक्ष (श्राद्ध पक्ष)',
      regionalName: 'Mahalaya Paksha',
      regionalNameHi: 'महालय पक्ष',
      lunarMonth: masa,
      paksha: paksha,
      tithiNumber: tithiNumber,
      shraddhaTithiNameEn: 'None (Standard Day)',
      shraddhaTithiNameHi: 'कोई नहीं (सामान्य दिवस)',
      description: 'Pitru Paksha is not active on this date.',
      descriptionHi: 'आज पितृ पक्ष सक्रिय नहीं है।',
      tarpanGuidanceEn: 'Standard daily prayers and Nitya Deva-Rishi-Pitru Tarpan may be observed.',
      tarpanGuidanceHi: 'नित्य देव-ऋषि-पितृ तर्पण एवं नित्य पूजा की जा सकती है।',
      applicabilityNote: 'Standard astrological and Muhurta rules apply.',
      applicabilityNoteHi: 'सामान्य ज्योतिषीय एवं मुहूर्त नियम लागू होते हैं।',
      classicalCitations: ['Nirnaya Sindhu', 'Dharma Sindhu', 'Matsya Purana']
    };
  }

  // Calculate Kutupa, Rohina and Aparahna timings from daytime interval
  const dayDurationMs = Math.max(1, sunset.getTime() - sunrise.getTime());
  const oneMuhurtaMs = dayDurationMs / 15;

  const kutupaStart = new Date(sunrise.getTime() + 7 * oneMuhurtaMs);
  const kutupaEnd = new Date(sunrise.getTime() + 8 * oneMuhurtaMs);
  const rohinaStart = new Date(sunrise.getTime() + 8 * oneMuhurtaMs);
  const rohinaEnd = new Date(sunrise.getTime() + 9 * oneMuhurtaMs);
  const aparahnaStart = new Date(sunrise.getTime() + 8 * oneMuhurtaMs);
  const aparahnaEnd = new Date(sunrise.getTime() + 12 * oneMuhurtaMs);

  const kutupaMuhurta: PitruPakshaMuhurtaWindow = {
    start: formatTime(kutupaStart, false, undefined, timezoneOffsetMinutes),
    end: formatTime(kutupaEnd, false, undefined, timezoneOffsetMinutes),
    durationMinutes: Math.round(oneMuhurtaMs / 60000)
  };

  const rohinaMuhurta: PitruPakshaMuhurtaWindow = {
    start: formatTime(rohinaStart, false, undefined, timezoneOffsetMinutes),
    end: formatTime(rohinaEnd, false, undefined, timezoneOffsetMinutes),
    durationMinutes: Math.round(oneMuhurtaMs / 60000)
  };

  const aparahnaKala: PitruPakshaMuhurtaWindow = {
    start: formatTime(aparahnaStart, false, undefined, timezoneOffsetMinutes),
    end: formatTime(aparahnaEnd, false, undefined, timezoneOffsetMinutes),
    durationMinutes: Math.round((4 * oneMuhurtaMs) / 60000)
  };

  let shraddhaTithiNameEn = 'Shraddha Tithi';
  let shraddhaTithiNameHi = 'श्राद्ध तिथि';
  let shraddhaSpecialDesignationEn: string | undefined;
  let shraddhaSpecialDesignationHi: string | undefined;

  if (masa === 'Bhadrapada' && tithiRawName === 'Purnima') {
    shraddhaTithiNameEn = 'Purnima Shraddha';
    shraddhaTithiNameHi = 'पूर्णिमा श्राद्ध';
    shraddhaSpecialDesignationEn = 'Mahalaya Arambha / Proshthapadi Purnima Shraddha';
    shraddhaSpecialDesignationHi = 'महालय आरम्भ / प्रौष्ठपदी पूर्णिमा श्राद्ध';
  } else {
    // Ashvina Krishna Paksha Tithis 1..15 (tithiNumber 16..30 or tithi 1..15 in Krishna)
    const kIndex = tithiNumber > 15 ? tithiNumber - 15 : tithiNumber;
    switch (kIndex) {
      case 1:
        shraddhaTithiNameEn = 'Pratipada Shraddha';
        shraddhaTithiNameHi = 'प्रतिपदा श्राद्ध';
        shraddhaSpecialDesignationEn = 'Nana-Nani Shraddha (Maternal Grandparents)';
        shraddhaSpecialDesignationHi = 'नाना-नानी श्राद्ध';
        break;
      case 2:
        shraddhaTithiNameEn = 'Dwitiya Shraddha';
        shraddhaTithiNameHi = 'द्वितीया श्राद्ध';
        break;
      case 3:
        shraddhaTithiNameEn = 'Tritiya Shraddha';
        shraddhaTithiNameHi = 'तृतीया श्राद्ध';
        break;
      case 4:
        shraddhaTithiNameEn = 'Chaturthi Shraddha';
        shraddhaTithiNameHi = 'चतुर्थी श्राद्ध';
        if (nakshatraName.includes('Bharani')) {
          shraddhaSpecialDesignationEn = 'Maha Bharani Shraddha';
          shraddhaSpecialDesignationHi = 'महाभरणी श्राद्ध';
        }
        break;
      case 5:
        shraddhaTithiNameEn = 'Panchami Shraddha';
        shraddhaTithiNameHi = 'पंचमी श्राद्ध';
        shraddhaSpecialDesignationEn = 'Kuwara Panchami (For unmarried deceased)';
        shraddhaSpecialDesignationHi = 'कुंवारा पंचमी';
        break;
      case 6:
        shraddhaTithiNameEn = 'Shashthi Shraddha';
        shraddhaTithiNameHi = 'षष्ठी श्राद्ध';
        shraddhaSpecialDesignationEn = 'Chhath Shraddha';
        shraddhaSpecialDesignationHi = 'छठ श्राद्ध';
        break;
      case 7:
        shraddhaTithiNameEn = 'Saptami Shraddha';
        shraddhaTithiNameHi = 'सप्तमी श्राद्ध';
        break;
      case 8:
        shraddhaTithiNameEn = 'Ashtami Shraddha';
        shraddhaTithiNameHi = 'अष्टमी श्राद्ध';
        shraddhaSpecialDesignationEn = 'Madhyashtami Shraddha';
        shraddhaSpecialDesignationHi = 'मध्याष्टमी श्राद्ध';
        break;
      case 9:
        shraddhaTithiNameEn = 'Matri Navami Shraddha';
        shraddhaTithiNameHi = 'मातृ नवमी श्राद्ध';
        shraddhaSpecialDesignationEn = 'Avadhava Navami (Dedicated to mothers and married women)';
        shraddhaSpecialDesignationHi = 'अविधवा नवमी (मातृ श्राद्ध)';
        break;
      case 10:
        shraddhaTithiNameEn = 'Dashami Shraddha';
        shraddhaTithiNameHi = 'दशमी श्राद्ध';
        break;
      case 11:
        shraddhaTithiNameEn = 'Ekadashi Shraddha';
        shraddhaTithiNameHi = 'एकादशी श्राद्ध';
        shraddhaSpecialDesignationEn = 'Indira Ekadashi Shraddha';
        shraddhaSpecialDesignationHi = 'इन्दिरा एकादशी श्राद्ध';
        break;
      case 12:
        shraddhaTithiNameEn = 'Dwadashi Shraddha';
        shraddhaTithiNameHi = 'द्वादशी श्राद्ध';
        shraddhaSpecialDesignationEn = 'Yati / Sanyasi Shraddha (For ascetics and renunciants)';
        shraddhaSpecialDesignationHi = 'यति / संन्यासी श्राद्ध';
        break;
      case 13:
        shraddhaTithiNameEn = 'Trayodashi Shraddha';
        shraddhaTithiNameHi = 'त्रयोदशी श्राद्ध';
        shraddhaSpecialDesignationEn = 'Magha Trayodashi / Kakabali (For deceased children)';
        shraddhaSpecialDesignationHi = 'काकबलि / बाल श्राद्ध';
        break;
      case 14:
        shraddhaTithiNameEn = 'Ghata Chaturdashi Shraddha';
        shraddhaTithiNameHi = 'घट चतुर्दशी श्राद्ध';
        shraddhaSpecialDesignationEn = 'Shastrahata Shraddha (For those who died by arms or accidents)';
        shraddhaSpecialDesignationHi = 'शस्त्रहत / अकाल मृत्यु श्राद्ध';
        break;
      case 15:
      case 30:
        shraddhaTithiNameEn = 'Sarvapitru Amavasya';
        shraddhaTithiNameHi = 'सर्वपितृ अमावस्या';
        shraddhaSpecialDesignationEn = 'Mahalaya Shraddha & Pitru Visargan (Universal homage for all ancestors)';
        shraddhaSpecialDesignationHi = 'महालय श्राद्ध एवं पितृ विसर्जन';
        break;
      default:
        shraddhaTithiNameEn = `${tithiRawName} Shraddha`;
        shraddhaTithiNameHi = `${tithiRawName} श्राद्ध`;
    }
  }

  return {
    isActive: true,
    observanceName: 'Pitru Paksha (Shraddha Paksha)',
    observanceNameHi: 'पितृ पक्ष (श्राद्ध पक्ष)',
    regionalName: 'Mahalaya Paksha',
    regionalNameHi: 'महालय पक्ष',
    lunarMonth: masa,
    paksha: paksha,
    tithiNumber: tithiNumber,
    shraddhaTithiNameEn,
    shraddhaTithiNameHi,
    shraddhaSpecialDesignationEn,
    shraddhaSpecialDesignationHi,
    kutupaMuhurta,
    rohinaMuhurta,
    aparahnaKala,
    description: `A sacred 16-lunar-day period dedicated to ancestral homage, tarpan, and gratitude rituals. Today's designated observance is ${shraddhaTithiNameEn}.`,
    descriptionHi: `पूर्वजों के प्रति कृतज्ञता, तर्पण एवं श्राद्ध हेतु समर्पित १६ दिवसीय महालय काल। आज की विहित श्राद्ध तिथि: ${shraddhaTithiNameHi}।`,
    tarpanGuidanceEn: 'Offer Til-Jal (water with black sesame and kusha grass) facing South during Kutupa and Aparahna kala. Perform Pancha Bali (offerings to cow, dog, crow, devas, and ants) followed by Anna-Vastra Daan to eligible recipients.',
    tarpanGuidanceHi: 'कुतुप एवं अपराह्न काल में दक्षिण दिशाभिमुख होकर कुश एवं काले तिल युक्त जल से पितरों को तर्पण अर्पित करें। तदुपरांत पंचबलि (गाय, श्वान, कौआ, देवादि, चींटियों) को भोग एवं जरूरतमंदों को अन्न-वस्त्र दान करें।',
    applicabilityNote: 'Highly meritorious for filial piety, ancestral peace, charitable donations (Daan), and inner spiritual contemplation. Traditional Jyotish Shastras advise prioritizing ancestral obligations over new celebratory ventures.',
    applicabilityNoteHi: 'पितृ शांति, तर्पण, दान, जप एवं सात्विक साधना हेतु परम पुण्यदायी काल। शास्त्रीय परम्परा अनुसार इस पक्ष में नूतन उत्सवों की अपेक्षा पूर्वज ऋण मुक्ति एवं आत्म-चिंतन को प्राथमिकता दी जाती है।',
    classicalCitations: [
      'Nirnaya Sindhu (Shraddha Prakarana)',
      'Dharma Sindhu (Mahalaya Vidhi)',
      'Matsya Purana (Chapter 18)',
      'Garuda Purana (Preta Khanda)'
    ]
  };
}

// ============================================================================
// SURYA SANKRANTI & PUNYA KAAL ENGINE (PAN-SANKRANTI-01)
// ============================================================================

export const SANKRANTI_METADATA: Record<number, {
  nameEn: string;
  nameHi: string;
  nameSa: string;
  signEn: string;
  signHi: string;
  classification: 'UTTARAYANA_START' | 'DAKSHINAYANA_START' | 'VISHUVA' | 'STANDARD';
  significanceEn: string;
  significanceHi: string;
  recommendedRitesEn: string[];
  recommendedRitesHi: string[];
}> = {
  0: {
    nameEn: 'Mesha Sankranti (Solar New Year)',
    nameHi: 'मेष संक्रांति (महाविषुव / सौर नववर्ष)',
    nameSa: 'मेषसंक्रान्तिः',
    signEn: 'Mesha (Aries)',
    signHi: 'मेष',
    classification: 'VISHUVA',
    significanceEn: 'Marks the Sun entering Aries (exaltation sign) and the Maha Vishuva equinox. Celebrated as the traditional Solar New Year across Bharat.',
    significanceHi: 'सूर्य का मेष राशि में प्रवेश एवं महाविषुव संक्रांति। इस दिन से सौर नववर्ष का शुभारंभ होता है।',
    recommendedRitesEn: ['Holy river bath (Snana)', 'Donation of sattu, earthen water pots (Ghat-Dāna), umbrella and hand-fans', 'Surya Puja & Gayatri Japa'],
    recommendedRitesHi: ['पवित्र नदी स्नान', 'सत्तू, जलपूर्ण घट, छाता एवं पंखा दान', 'सूर्य पूजन एवं गायत्री जप']
  },
  1: {
    nameEn: 'Vrishabha Sankranti',
    nameHi: 'वृषभ संक्रांति',
    nameSa: 'वृषभसंक्रान्तिः',
    signEn: 'Vrishabha (Taurus)',
    signHi: 'वृषभ',
    classification: 'STANDARD',
    significanceEn: 'Marks the Sun transiting into Taurus. Auspicious for Gau-Dāna (cow veneration/charity) and spiritual sadhana.',
    significanceHi: 'सूर्य का वृषभ राशि में प्रवेश। गो-सेवा, गो-दान एवं सात्विक साधना हेतु शुभ।',
    recommendedRitesEn: ['Gau Seva & donation', 'Punya Kaal meditation', 'Offering water to Surya Dev'],
    recommendedRitesHi: ['गो सेवा एवं दान', 'पुण्यकाल ध्यान एवं जप', 'सूर्य देव को अर्घ्यदान']
  },
  2: {
    nameEn: 'Mithuna Sankranti',
    nameHi: 'मिथुन संक्रांति (रज संक्रांति)',
    nameSa: 'मिथुनसंक्रान्तिः',
    signEn: 'Mithuna (Gemini)',
    signHi: 'मिथुन',
    classification: 'STANDARD',
    significanceEn: 'Marks Sun ingress into Gemini. Celebrated as Raja Parba in Odisha, honouring Mother Earth and creative fertility.',
    significanceHi: 'सूर्य का मिथुन राशि में प्रवेश। उड़ीसा में यह पर्व राजा पर्ब के रूप में भू-देवी के सम्मान में मनाया जाता है।',
    recommendedRitesEn: ['Earth veneration (Bhumi Puja)', 'Snana and Dāna', 'Vishnu and Surya worship'],
    recommendedRitesHi: ['भूमि पूजन एवं पर्यावरण संरक्षण', 'स्नान-दान', 'भगवान विष्णु एवं सूर्य नारायण की आराधना']
  },
  3: {
    nameEn: 'Karka Sankranti (Dakshinayana Arambha)',
    nameHi: 'कर्क संक्रांति (दक्षिणायन आरंभ)',
    nameSa: 'कर्कसंक्रान्तिः',
    signEn: 'Karka (Cancer)',
    signHi: 'कर्क',
    classification: 'DAKSHINAYANA_START',
    significanceEn: 'Marks the Sun transiting into Cancer and the astronomical commencement of Dakshinayana (the southern course). Ideal for Pitru worship and introspection.',
    significanceHi: 'सूर्य का कर्क राशि में प्रवेश एवं दक्षिणायन का प्रारंभ। इस काल में पितृ तर्पण, ध्यान और आत्मनिरीक्षण का विशेष महत्व है।',
    recommendedRitesEn: ['Pitru Tarpan & Shraddha offerings', 'Charity of food grains and clothes', 'Shiva Puja and spiritual tapas'],
    recommendedRitesHi: ['पितृ तर्पण एवं श्राद्ध कर्म', 'अन्न-वस्त्र दान', 'शिव पूजन एवं तप-साधना']
  },
  4: {
    nameEn: 'Simha Sankranti',
    nameHi: 'सिंह संक्रांति (घृत संक्रांति)',
    nameSa: 'सिंहसंक्रान्तिः',
    signEn: 'Simha (Leo)',
    signHi: 'सिंह',
    classification: 'STANDARD',
    significanceEn: 'Marks the Sun entering its own domicile of Leo. Known as Ghee Sankranti in Uttarakhand, symbolizing health, vitality and divine brilliance.',
    significanceHi: 'सूर्य का स्वराशि सिंह में प्रवेश। उत्तराखंड में इसे घृत संक्रांति के रूप में मनाया जाता है। आरोग्य एवं तेज की वृद्धि का दिन।',
    recommendedRitesEn: ['Surya Arghya with red sandalwood & flowers', 'Consumption and donation of pure Ghee', 'Aditya Hridaya Stotra recitation'],
    recommendedRitesHi: ['रक्त चंदन एवं पुष्पों से सूर्य को अर्घ्य', 'शुद्ध घी का सेवन एवं दान', 'आदित्य हृदय स्तोत्र का पाठ']
  },
  5: {
    nameEn: 'Kanya Sankranti',
    nameHi: 'कन्या संक्रांति (विश्वकर्मा जयंती)',
    nameSa: 'कन्यासंक्रान्तिः',
    signEn: 'Kanya (Virgo)',
    signHi: 'कन्या',
    classification: 'STANDARD',
    significanceEn: 'Marks the Sun entering Virgo. Frequently coincides with Lord Vishwakarma Puja, honouring divine craftsmanship, engineering, and arts.',
    significanceHi: 'सूर्य का कन्या राशि में प्रवेश। भगवान विश्वकर्मा की पूजा एवं शिल्प, कौशल और यंत्रों के पूजन का शुभ दिवस।',
    recommendedRitesEn: ['Vishwakarma Puja & tool consecration', 'Charity to artisans and workers', 'Surya Namaskar & charity'],
    recommendedRitesHi: ['विश्वकर्मा पूजन एवं औजारों का सम्मान', 'श्रमजीवियों को दान एवं सम्मान', 'सूर्य नमस्कार एवं दान']
  },
  6: {
    nameEn: 'Tula Sankranti (Jala Vishuva)',
    nameHi: 'तुला संक्रांति (जलविषुव संक्रांति)',
    nameSa: 'तुलासंक्रान्तिः',
    signEn: 'Tula (Libra)',
    signHi: 'तुला',
    classification: 'VISHUVA',
    significanceEn: 'Marks the Sun entering Libra (Jala Vishuva / Autumnal Equinox balance). Celebrated in South India as Kaveri Sankramana.',
    significanceHi: 'सूर्य का तुला राशि में प्रवेश एवं जल विषुव काल। दक्षिण भारत में इसे पवित्र कावेरी संक्रमण के रूप में मनाया जाता है।',
    recommendedRitesEn: ['Sacred river bath (Kaveri/Ganga snana)', 'Annadāna (food donation) to the needy', 'Lakshmi-Narayana worship'],
    recommendedRitesHi: ['पवित्र नदियों (कावेरी/गंगा) में स्नान', 'अन्नदान एवं दीपदान', 'लक्ष्मी-नारायण पूजन']
  },
  7: {
    nameEn: 'Vrishchika Sankranti',
    nameHi: 'वृश्चिक संक्रांति',
    nameSa: 'वृश्चिकसंक्रान्तिः',
    signEn: 'Vrishchika (Scorpio)',
    signHi: 'वृश्चिक',
    classification: 'STANDARD',
    significanceEn: 'Marks the Sun transiting into Scorpio. Highly auspicious for overcoming inner impurities, penance, and water charity.',
    significanceHi: 'सूर्य का वृश्चिक राशि में प्रवेश। आत्म-शोधन, तप, जप एवं जल-अन्न दान हेतु उत्तम दिवस।',
    recommendedRitesEn: ['Snana in holy streams', 'Water and jaggery donation', 'Gayatri Mantra purashcharana'],
    recommendedRitesHi: ['तीर्थ स्नान', 'जल एवं गुड़ का दान', 'गायत्री मंत्र जप']
  },
  8: {
    nameEn: 'Dhanu Sankranti (Kharmas / Dhanurmas Arambha)',
    nameHi: 'धनु संक्रांति (धनुर्मास / खरमास आरंभ)',
    nameSa: 'धनुःसंक्रान्तिः',
    signEn: 'Dhanu (Sagittarius)',
    signHi: 'धनु',
    classification: 'STANDARD',
    significanceEn: 'Marks the Sun entering Jupiter-ruled Sagittarius, initiating Dhanurmasa / Kharmas. Dedicated to elevated spiritual sadhana and Gita recitation.',
    significanceHi: 'सूर्य का गुरु की राशि धनु में प्रवेश। धनुर्मास / खरमास का आरंभ। सांसारिक कार्यों से अधिक भगवद्-भक्ति एवं गीता पाठ का समय।',
    recommendedRitesEn: ['Shrimad Bhagavad Gita path', 'Vishnu Sahasranama recitation', 'Donation of warm clothes and sesame to the needy'],
    recommendedRitesHi: ['श्रीमद्भगवद्गीता पाठ', 'विष्णु सहस्रनाम जप', 'ऊनी वस्त्र एवं तिल दान']
  },
  9: {
    nameEn: 'Makara Sankranti (Uttarayana Arambha)',
    nameHi: 'मकर संक्रांति (उत्तरायण आरंभ / पोंगल / माघी)',
    nameSa: 'मकरसंक्रान्तिः',
    signEn: 'Makara (Capricorn)',
    signHi: 'मकर',
    classification: 'UTTARAYANA_START',
    significanceEn: 'Celebrates the Sun entering Capricorn and the commencement of Uttarayana (the divine Northern Course of the Sun). Supreme day for holy snana and Tila-Dāna.',
    significanceHi: 'सूर्य का मकर राशि में प्रवेश एवं देवायन/उत्तरायण का महापर्व। गंगा स्नान, तिल-गुड़ दान, सूर्योपासना एवं खिचड़ी दान का सर्वोच्च महात्म्य।',
    recommendedRitesEn: ['Ganga/Tirtha Snana at sunrise', 'Tila (sesame), jaggery, khichdi, and blanket charity', 'Surya Puja, Surya Gayatri & Aditya Hridaya Stotra'],
    recommendedRitesHi: ['सूर्योदय कालीन तीर्थ स्नान', 'तिल, गुड़, खिचड़ी एवं कम्बल दान', 'सूर्य पूजन, सूर्य गायत्री एवं आदित्य हृदय स्तोत्र पाठ']
  },
  10: {
    nameEn: 'Kumbha Sankranti',
    nameHi: 'कुम्भ संक्रांति',
    nameSa: 'कुम्भसंक्रान्तिः',
    signEn: 'Kumbha (Aquarius)',
    signHi: 'कुम्भ',
    classification: 'STANDARD',
    significanceEn: 'Marks the Sun entering Aquarius. Auspicious for pilgrimage, river snana, and sharing knowledge and nourishment.',
    significanceHi: 'सूर्य का कुम्भ राशि में प्रवेश। तीर्थ स्नान, ज्ञान-दान एवं भोजन दान हेतु विशेष फलप्रद।',
    recommendedRitesEn: ['Holy river bath', 'Charity of footwear, food, and utensils', 'Meditation on Shiva and Surya'],
    recommendedRitesHi: ['पवित्र नदी स्नान', 'पात्र, अन्न एवं वस्त्र दान', 'भगवान शिव एवं सूर्य का ध्यान']
  },
  11: {
    nameEn: 'Meena Sankranti (Kharmas Arambha)',
    nameHi: 'मीन संक्रांति (खरमास / महाशिवरात्रि मास)',
    nameSa: 'मीनसंक्रान्तिः',
    signEn: 'Meena (Pisces)',
    signHi: 'मीन',
    classification: 'STANDARD',
    significanceEn: 'Marks the ingress of the Sun into Pisces (the final sign of the zodiac), completing the annual solar journey. Auspicious for dhyana and charity.',
    significanceHi: 'सूर्य का मीन राशि में प्रवेश। सौर वर्ष का अंतिम संक्रमण। दान, जप, तीर्थाटन एवं आत्म-शुद्धि हेतु विशेष पुण्यदायी।',
    recommendedRitesEn: ['Charity of gold, land, or yellow grains/fruits', 'Snana in sacred rivers', 'Vishnu and Surya worship'],
    recommendedRitesHi: ['पीत वस्त्र, चने की दाल एवं फलों का दान', 'पवित्र नदी स्नान', 'विष्णु एवं सूर्य उपासना']
  }
};

/**
 * Calculates authoritative Surya Sankranti & Punya Kaal context for a target date
 */
export function calculateAuthoritativeSankrantiContext(
  targetDate: Date,
  timezoneOffsetMinutes: number = 330,
  ayanamshaSystemOverride?: string
): SankrantiInfo {
  const { startUtc, endUtc } = getCanonicalCivilDayUtcBounds(targetDate, timezoneOffsetMinutes);

  const getSunSiderealLon = (d: Date): number => {
    const time = Astronomy.MakeTime(d);
    const sunEcl = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Sun, time, true));
    const ayanamsha = getCanonicalAyanamsha(time, ayanamshaSystemOverride);
    return (sunEcl.elon - ayanamsha + 360) % 360;
  };

  const windowStart = new Date(startUtc.getTime() - 18 * 3600 * 1000);
  const windowEnd = new Date(endUtc.getTime() + 18 * 3600 * 1000);

  const startLon = getSunSiderealLon(windowStart);
  const endLon = getSunSiderealLon(windowEnd);

  const startSignIdx = Math.floor(startLon / 30);
  const endSignIdx = Math.floor(endLon / 30);

  let exactIngress: Date | null = null;
  let targetSignIdx = -1;

  if (startSignIdx !== endSignIdx || Math.abs(endLon - startLon) > 20) {
    let low = windowStart.getTime();
    let high = windowEnd.getTime();

    for (let i = 0; i < 22; i++) {
      const mid = (low + high) / 2;
      const midLon = getSunSiderealLon(new Date(mid));
      const midSignIdx = Math.floor(midLon / 30);

      if (midSignIdx === startSignIdx) {
        low = mid;
      } else {
        high = mid;
        targetSignIdx = midSignIdx;
      }
    }
    exactIngress = new Date((low + high) / 2);
  }

  if (exactIngress && targetSignIdx >= 0) {
    const meta = SANKRANTI_METADATA[targetSignIdx];
    let punyaStart: Date;
    let punyaEnd: Date;
    let mahaStart: Date;
    let mahaEnd: Date;

    if (meta.classification === 'UTTARAYANA_START') {
      punyaStart = exactIngress;
      punyaEnd = new Date(exactIngress.getTime() + 8 * 3600 * 1000);
      mahaStart = exactIngress;
      mahaEnd = new Date(exactIngress.getTime() + 2 * 3600 * 1000);
    } else if (meta.classification === 'DAKSHINAYANA_START') {
      punyaStart = new Date(exactIngress.getTime() - 8 * 3600 * 1000);
      punyaEnd = exactIngress;
      mahaStart = new Date(exactIngress.getTime() - 2 * 3600 * 1000);
      mahaEnd = exactIngress;
    } else if (meta.classification === 'VISHUVA') {
      punyaStart = new Date(exactIngress.getTime() - 6 * 3600 * 1000);
      punyaEnd = new Date(exactIngress.getTime() + 6 * 3600 * 1000);
      mahaStart = new Date(exactIngress.getTime() - 1.5 * 3600 * 1000);
      mahaEnd = new Date(exactIngress.getTime() + 1.5 * 3600 * 1000);
    } else {
      punyaStart = new Date(exactIngress.getTime() - 6 * 3600 * 1000);
      punyaEnd = new Date(exactIngress.getTime() + 6 * 3600 * 1000);
      mahaStart = new Date(exactIngress.getTime() - 1 * 3600 * 1000);
      mahaEnd = new Date(exactIngress.getTime() + 1 * 3600 * 1000);
    }

    const isIngressOnDay = exactIngress.getTime() >= startUtc.getTime() && exactIngress.getTime() <= endUtc.getTime();
    const isPunyaKaalOverlapping = punyaEnd.getTime() >= startUtc.getTime() && punyaStart.getTime() <= endUtc.getTime();

    if (isIngressOnDay || isPunyaKaalOverlapping) {
      const fromSignIdx = (targetSignIdx - 1 + 12) % 12;
      const fromMeta = SANKRANTI_METADATA[fromSignIdx];

      const formattedIngressTime = formatTime(exactIngress, false, undefined, timezoneOffsetMinutes);
      const formattedPunyaKaal = `${formatTime(punyaStart, false, undefined, timezoneOffsetMinutes)} – ${formatTime(punyaEnd, false, undefined, timezoneOffsetMinutes)}`;
      const formattedMahaPunyaKaal = `${formatTime(mahaStart, false, undefined, timezoneOffsetMinutes)} – ${formatTime(mahaEnd, false, undefined, timezoneOffsetMinutes)}`;

      const currentInstant = targetDate.getTime();
      const isPunyaKaalActive = currentInstant >= punyaStart.getTime() && currentInstant <= punyaEnd.getTime();

      return {
        isSankrantiDay: true,
        sankrantiNameEn: meta.nameEn,
        sankrantiNameHi: meta.nameHi,
        sankrantiNameSa: meta.nameSa,
        fromSignEn: fromMeta.signEn,
        fromSignHi: fromMeta.signHi,
        toSignEn: meta.signEn,
        toSignHi: meta.signHi,
        ingressTimestamp: exactIngress,
        formattedIngressTime,
        punyaKaalStart: punyaStart,
        punyaKaalEnd: punyaEnd,
        formattedPunyaKaal,
        mahaPunyaKaalStart: mahaStart,
        mahaPunyaKaalEnd: mahaEnd,
        formattedMahaPunyaKaal,
        isPunyaKaalActive,
        classification: meta.classification,
        significanceEn: meta.significanceEn,
        significanceHi: meta.significanceHi,
        recommendedRitesEn: meta.recommendedRitesEn,
        recommendedRitesHi: meta.recommendedRitesHi
      };
    }
  }

  return {
    isSankrantiDay: false,
    sankrantiNameEn: '',
    sankrantiNameHi: '',
    sankrantiNameSa: '',
    fromSignEn: '',
    fromSignHi: '',
    toSignEn: '',
    toSignHi: '',
    isPunyaKaalActive: false,
    classification: 'STANDARD',
    significanceEn: 'Standard solar transit day. No major planetary sign ingress active today.',
    significanceHi: 'सामान्य सौर दिवस। आज कोई प्रमुख राशि संक्रांति सक्रिय नहीं है।',
    recommendedRitesEn: ['Nitya Surya Arghya at sunrise', 'Gayatri Mantra Japa', 'Daily charitable deeds'],
    recommendedRitesHi: ['नित्य सूर्य अर्घ्यदान', 'गायत्री मंत्र जप', 'नित्य सत्कर्म एवं दान']
  };
}

function buildCacheKey(
  date: Date,
  coords: PanchangaCoordinates,
  ayanamshaSystem?: string
): string {
  const dStr = date.toISOString().slice(0, 13); // 1-hour temporal bucket
  const latStr = coords.latitude.toFixed(3);
  const lonStr = coords.longitude.toFixed(3);
  const elevStr = (coords.elevationMeters || 0).toString();
  const tzStr = (coords.timezoneOffsetMinutes ?? 330).toString();
  const ayanStr = ayanamshaSystem || 'lahiri';
  const profileKey = coords.profileStorageId || 'public';
  return `${dStr}_${latStr}_${lonStr}_${elevStr}_${tzStr}_${ayanStr}_${profileKey}`;
}

// ============================================================================
// 8. MASTER AUTHORITATIVE CALCULATION ENTRY POINT
// ============================================================================

/**
 * Single authoritative Panchanga evaluation function for all product surfaces
 */
export function calculateAuthoritativePanchanga(
  targetDate: Date,
  coordinates: PanchangaCoordinates,
  ayanamshaSystemOverride?: string
): AuthoritativePanchanga {
  const cacheKey = buildCacheKey(targetDate, coordinates, ayanamshaSystemOverride);
  const cached = PANCHANGA_CACHE.get(cacheKey);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.data;
  }

  const { latitude, longitude, elevationMeters = 0 } = coordinates;

  // 1. Astronomical Sun times
  const { sunrise, sunset, solarNoon } = calculateAuthoritativeSunTimes(
    targetDate, 
    latitude, 
    longitude, 
    elevationMeters,
    coordinates.timezoneOffsetMinutes ?? 330
  );

  // 2. Ephemeris & Ayanamsha
  const ephemeris = calculateAuthoritativeSunMoon(targetDate, ayanamshaSystemOverride);

  // 3. Five Angas
  const tithi = calculateCanonicalTithi(
    ephemeris.sunLongitude, 
    ephemeris.moonLongitude, 
    targetDate, 
    ayanamshaSystemOverride
  );

  // Compute all intra-day Tithi transitions spanning the civil day
  const tithiTransitions: TithiTransition[] = [];
  let evalTime = getCanonicalCivilDayUtcBounds(targetDate, coordinates.timezoneOffsetMinutes ?? 330).startUtc;
  const civilDayEnd = getCanonicalCivilDayUtcBounds(targetDate, coordinates.timezoneOffsetMinutes ?? 330).endUtc;

  while (evalTime < civilDayEnd && tithiTransitions.length < 5) {
    const ephemSnap = calculateAuthoritativeSunMoon(evalTime, ayanamshaSystemOverride);
    const tFactor = calculateCanonicalTithi(ephemSnap.sunLongitude, ephemSnap.moonLongitude, evalTime, ayanamshaSystemOverride);
    const startDt = new Date(evalTime);
    const endDt = tFactor.endDateTime || new Date(evalTime.getTime() + 12 * 3600 * 1000);

    tithiTransitions.push({
      number: tFactor.number,
      name: tFactor.name,
      rawName: tFactor.rawName,
      paksha: tFactor.paksha,
      startDateTime: startDt,
      endDateTime: endDt,
      formattedStartTime: formatTime(startDt, false, undefined, coordinates.timezoneOffsetMinutes ?? 330),
      formattedEndTime: tFactor.formattedEndTime ? formatTime(endDt, false, undefined, coordinates.timezoneOffsetMinutes ?? 330) : formatTime(endDt, false, undefined, coordinates.timezoneOffsetMinutes ?? 330)
    });

    if (endDt.getTime() >= civilDayEnd.getTime()) {
      break;
    }
    evalTime = new Date(endDt.getTime() + 60000);
  }

  // Compute all intra-day Nakshatra transitions spanning the civil day
  const nakshatraTransitions: NakshatraTransition[] = [];
  let nakEvalTime = getCanonicalCivilDayUtcBounds(targetDate, coordinates.timezoneOffsetMinutes ?? 330).startUtc;
  const nakCivilDayEnd = getCanonicalCivilDayUtcBounds(targetDate, coordinates.timezoneOffsetMinutes ?? 330).endUtc;

  while (nakEvalTime < nakCivilDayEnd && nakshatraTransitions.length < 5) {
    const ephemSnap = calculateAuthoritativeSunMoon(nakEvalTime, ayanamshaSystemOverride);
    const nFactor = calculateCanonicalNakshatra(ephemSnap.moonLongitude, nakEvalTime, ayanamshaSystemOverride);
    const startDt = new Date(nakEvalTime);
    const endDt = nFactor.endDateTime || new Date(nakEvalTime.getTime() + 24 * 3600 * 1000);

    nakshatraTransitions.push({
      index: nFactor.index,
      name: nFactor.name,
      pada: nFactor.pada,
      rulingPlanet: nFactor.rulingPlanet,
      startDateTime: startDt,
      endDateTime: endDt,
      formattedStartTime: formatTime(startDt, false, undefined, coordinates.timezoneOffsetMinutes ?? 330),
      formattedEndTime: nFactor.formattedEndTime ? formatTime(endDt, false, undefined, coordinates.timezoneOffsetMinutes ?? 330) : formatTime(endDt, false, undefined, coordinates.timezoneOffsetMinutes ?? 330)
    });

    if (endDt.getTime() >= nakCivilDayEnd.getTime()) {
      break;
    }
    nakEvalTime = new Date(endDt.getTime() + 60000);
  }

  // Compute all intra-day Yoga transitions spanning the civil day
  const yogaTransitions: YogaTransition[] = [];
  let yogaEvalTime = getCanonicalCivilDayUtcBounds(targetDate, coordinates.timezoneOffsetMinutes ?? 330).startUtc;
  const yogaCivilDayEnd = getCanonicalCivilDayUtcBounds(targetDate, coordinates.timezoneOffsetMinutes ?? 330).endUtc;

  while (yogaEvalTime < yogaCivilDayEnd && yogaTransitions.length < 5) {
    const ephemSnap = calculateAuthoritativeSunMoon(yogaEvalTime, ayanamshaSystemOverride);
    const yFactor = calculateCanonicalYoga(ephemSnap.sunLongitude, ephemSnap.moonLongitude, yogaEvalTime, ayanamshaSystemOverride);
    const startDt = new Date(yogaEvalTime);
    const endDt = yFactor.endDateTime || new Date(yogaEvalTime.getTime() + 24 * 3600 * 1000);

    yogaTransitions.push({
      index: yFactor.index,
      name: yFactor.name,
      startDateTime: startDt,
      endDateTime: endDt,
      formattedStartTime: formatTime(startDt, false, undefined, coordinates.timezoneOffsetMinutes ?? 330),
      formattedEndTime: yFactor.formattedEndTime ? formatTime(endDt, false, undefined, coordinates.timezoneOffsetMinutes ?? 330) : formatTime(endDt, false, undefined, coordinates.timezoneOffsetMinutes ?? 330)
    });

    if (endDt.getTime() >= yogaCivilDayEnd.getTime()) {
      break;
    }
    yogaEvalTime = new Date(endDt.getTime() + 60000);
  }

  // Compute all intra-day Karana transitions spanning the civil day
  const karanaTransitions: KaranaTransition[] = [];
  let karanaEvalTime = getCanonicalCivilDayUtcBounds(targetDate, coordinates.timezoneOffsetMinutes ?? 330).startUtc;
  const karanaCivilDayEnd = getCanonicalCivilDayUtcBounds(targetDate, coordinates.timezoneOffsetMinutes ?? 330).endUtc;

  while (karanaEvalTime < karanaCivilDayEnd && karanaTransitions.length < 8) {
    const ephemSnap = calculateAuthoritativeSunMoon(karanaEvalTime, ayanamshaSystemOverride);
    const kFactor = calculateCanonicalKarana(ephemSnap.sunLongitude, ephemSnap.moonLongitude, karanaEvalTime, ayanamshaSystemOverride);
    const startDt = new Date(karanaEvalTime);
    const endDt = kFactor.endDateTime || new Date(karanaEvalTime.getTime() + 12 * 3600 * 1000);

    karanaTransitions.push({
      index60: kFactor.index60,
      name: kFactor.name,
      type: kFactor.type,
      isVishtiBhadra: kFactor.isVishtiBhadra,
      startDateTime: startDt,
      endDateTime: endDt,
      formattedStartTime: formatTime(startDt, false, undefined, coordinates.timezoneOffsetMinutes ?? 330),
      formattedEndTime: kFactor.formattedEndTime ? formatTime(endDt, false, undefined, coordinates.timezoneOffsetMinutes ?? 330) : formatTime(endDt, false, undefined, coordinates.timezoneOffsetMinutes ?? 330)
    });

    if (endDt.getTime() >= karanaCivilDayEnd.getTime()) {
      break;
    }
    karanaEvalTime = new Date(endDt.getTime() + 60000);
  }
  
  const nakshatra = calculateCanonicalNakshatra(
    ephemeris.moonLongitude, 
    targetDate, 
    ayanamshaSystemOverride
  );
  
  const yoga = calculateCanonicalYoga(
    ephemeris.sunLongitude, 
    ephemeris.moonLongitude, 
    targetDate, 
    ayanamshaSystemOverride
  );
  
  const karana = calculateCanonicalKarana(
    ephemeris.sunLongitude, 
    ephemeris.moonLongitude, 
    targetDate, 
    ayanamshaSystemOverride
  );
  
  const vara = calculateCanonicalVara(targetDate, sunrise, coordinates.timezoneOffsetMinutes ?? 330);

  // 4. Time Windows (Rahu Kalam, Yamaganda, Gulika, Abhijit)
  const { rahuKalam, yamaganda, gulikaKalam, abhijit } = calculateCanonicalTimeWindows(
    sunrise, 
    sunset, 
    vara.dayOfWeek
  );

  // 5. Choghadiya and Hora
  const choghadiya = calculateCanonicalChoghadiya(targetDate, sunrise, sunset, vara.dayOfWeek);
  const hora = calculateCanonicalHora(targetDate, sunrise, vara.dayOfWeek);

  // 6. Calendar framework (Masa, Samvat, Ritu, Dish-Shool, Ayana)
  const sunSignIdx = Math.floor(ephemeris.sunLongitude / 30);
  const masaIdx = (sunSignIdx + 1) % 12;
  const masa = VEDIC_MASAS[masaIdx];

  const { vikramSamvat, shakaSamvat } = calculateAuthoritativeSamvat(targetDate, masa, tithi.paksha);
  const samvatString = `${masa} ${tithi.paksha} ${tithi.rawName} • Vikram Samvat ${vikramSamvat}`;
  
  const { ayana, ayanaNameEn, ayanaNameHi } = calculateAuthoritativeAyana(ephemeris.sunLongitude);

  const ritu = RITUS.find(r => r.months.includes(masa)) || RITUS[0];
  const dishShool = DISH_SHOOL_BY_DAY[vara.dayOfWeek] || DISH_SHOOL_BY_DAY[0];

  const dayDurationHours = (sunset.getTime() - sunrise.getTime()) / 3600000;

  const evidence: PanchangaEvidence = {
    astronomicalEngine: 'Astronomy-Engine VSOP87/ELP2000-82b',
    ayanamshaSystem: ephemeris.ayanamshaSystem,
    ayanamshaDegrees: ephemeris.ayanamsha,
    observerLatitude: latitude,
    observerLongitude: longitude,
    observerElevationM: elevationMeters,
    sunriseUtc: sunrise.toISOString(),
    sunsetUtc: sunset.toISOString(),
    dayDurationHours: Number(dayDurationHours.toFixed(2)),
    calculationTimestamp: new Date().toISOString(),
    profileStorageId: coordinates.profileStorageId
  };

  const pitruPaksha = calculateAuthoritativePitruPakshaContext(
    targetDate,
    tithi.number,
    tithi.rawName,
    tithi.paksha,
    masa,
    nakshatra.name,
    sunrise,
    sunset,
    coordinates.timezoneOffsetMinutes ?? 330
  );

  const sunriseStr = formatTime(sunrise, false, undefined, coordinates.timezoneOffsetMinutes ?? 330);
  const vratObservance = calculateAuthoritativeVratObservance(
    targetDate,
    tithi.number,
    tithi.name,
    tithi.paksha,
    masa,
    vara.dayOfWeek,
    vara.nameEn,
    sunriseStr,
    ayanamshaSystemOverride
  );

  const sankranti = calculateAuthoritativeSankrantiContext(
    targetDate,
    coordinates.timezoneOffsetMinutes ?? 330,
    ayanamshaSystemOverride
  );

  const result: AuthoritativePanchanga = {
    targetDateTime: targetDate,
    coordinates,
    ephemeris,
    sunrise,
    sunset,
    solarNoon,
    tithi,
    tithiTransitions,
    nakshatra,
    nakshatraTransitions,
    yoga,
    yogaTransitions,
    karana,
    karanaTransitions,
    vara,
    rahuKalam,
    yamaganda,
    gulikaKalam,
    abhijit,
    choghadiya,
    hora,
    masa,
    paksha: tithi.paksha,
    vikramSamvat,
    shakaSamvat,
    samvatString,
    ritu,
    dishShool,
    ayana,
    ayanaNameEn,
    ayanaNameHi,
    evidence,
    pitruPaksha,
    vratObservance,
    sankranti
  };

  // Cache insertion
  if (PANCHANGA_CACHE.size >= MAX_CACHE_SIZE) {
    const oldestKey = PANCHANGA_CACHE.keys().next().value;
    if (oldestKey) PANCHANGA_CACHE.delete(oldestKey);
  }
  PANCHANGA_CACHE.set(cacheKey, {
    key: cacheKey,
    data: result,
    expiresAt: Date.now() + 15 * 60 * 1000 // 15-minute TTL
  });

  return result;
}
